// ============================================================================
// SKIPRES DATABASE - ARCHITETTURA MODULARE PULITA
// ============================================================================
// Versione: 2.0 - 18/02/2026
// Struttura ottimizzata e pronta per migrazione a Firebase
// ============================================================================

// ============================================================================
// SEZIONE 1: CONFIGURAZIONE MODULI
// ============================================================================
// I moduli definiscono le funzionalità disponibili per ogni Office.
// L'Admin assegna i moduli, il Partner configura i dettagli operativi.
// ============================================================================

export const MODULES = {
  // --------------------------------------------------------------------------
  // MODULO: SERVIZI AD APPUNTAMENTO
  // Per: parrucchieri, dentisti, meccanici, professionisti
  // --------------------------------------------------------------------------
  appointments: {
    id: 'appointments',
    name: 'Servizi a Prenotazione',
    description: 'Servizi con slot orari e prenotazione appuntamento',
    icon: 'Calendar',
    color: 'emerald',
    // Configurazione strutturale (Admin)
    schema: {
      requiresResource: true,
      resourceTypes: ['operator', 'room', 'equipment'],
      pricingType: 'fixed',
      bookingUnit: 'slot',
    },
    // Impostazioni configurabili dal Partner
    partnerSettings: {
      advanceBookingDays: { type: 'number', default: 30, label: 'Giorni prenotazione anticipata' },
      minBookingNotice: { type: 'number', default: 60, label: 'Preavviso minimo (minuti)' },
      requiresConfirmation: { type: 'boolean', default: false, label: 'Richiedi conferma manuale' },
      allowCancellation: { type: 'boolean', default: true, label: 'Consenti cancellazione' },
      cancellationNotice: { type: 'number', default: 24, label: 'Preavviso cancellazione (ore)' },
    },
  },

  // --------------------------------------------------------------------------
  // MODULO: NOLEGGI E PRENOTAZIONI GIORNALIERE
  // Per: hotel, B&B, noleggio auto/barche, case vacanza
  // --------------------------------------------------------------------------
  rentals: {
    id: 'rentals',
    name: 'Noleggi e Soggiorni',
    description: 'Prenotazioni giornaliere per noleggi, camere, appartamenti',
    icon: 'CalendarDays',
    color: 'blue',
    schema: {
      requiresResource: true,
      resourceTypes: ['room', 'vehicle', 'boat', 'apartment'],
      pricingType: 'daily',
      bookingUnit: 'day',
    },
    partnerSettings: {
      minStay: { type: 'number', default: 1, label: 'Soggiorno minimo (notti)' },
      maxStay: { type: 'number', default: 30, label: 'Soggiorno massimo (notti)' },
      checkInTime: { type: 'time', default: '14:00', label: 'Orario check-in' },
      checkOutTime: { type: 'time', default: '10:00', label: 'Orario check-out' },
      weekendMarkup: { type: 'number', default: 0, label: 'Maggiorazione weekend (%)' },
      depositRequired: { type: 'boolean', default: false, label: 'Richiedi caparra' },
      depositPercent: { type: 'number', default: 30, label: 'Percentuale caparra' },
    },
  },

  // --------------------------------------------------------------------------
  // MODULO: CORSI E ABBONAMENTI
  // Per: palestre, scuole, centri formativi
  // --------------------------------------------------------------------------
  courses: {
    id: 'courses',
    name: 'Corsi e Lezioni',
    description: 'Corsi con programmazione settimanale e abbonamenti',
    icon: 'GraduationCap',
    color: 'indigo',
    schema: {
      requiresResource: true,
      resourceTypes: ['instructor', 'room'],
      pricingType: 'subscription',
      bookingUnit: 'session',
    },
    partnerSettings: {
      allowTrialLesson: { type: 'boolean', default: true, label: 'Consenti lezione di prova' },
      trialPrice: { type: 'number', default: 0, label: 'Prezzo lezione prova' },
      maxParticipants: { type: 'number', default: 20, label: 'Partecipanti max per lezione' },
      allowDropIn: { type: 'boolean', default: false, label: 'Consenti ingresso singolo' },
      dropInPrice: { type: 'number', default: 15, label: 'Prezzo ingresso singolo' },
    },
  },

  // --------------------------------------------------------------------------
  // MODULO: EVENTI
  // Per: sale ricevimenti, teatri, organizzatori eventi
  // --------------------------------------------------------------------------
  events: {
    id: 'events',
    name: 'Eventi',
    description: 'Eventi singoli con data e ora specifica',
    icon: 'PartyPopper',
    color: 'orange',
    schema: {
      requiresResource: false,
      pricingType: 'ticket',
      bookingUnit: 'ticket',
    },
    partnerSettings: {
      maxTickets: { type: 'number', default: 100, label: 'Biglietti massimi' },
      earlyBirdDiscount: { type: 'number', default: 0, label: 'Sconto early bird (%)' },
      earlyBirdDays: { type: 'number', default: 30, label: 'Giorni early bird' },
    },
  },

  // --------------------------------------------------------------------------
  // MODULO: E-COMMERCE (con sub-moduli)
  // Per: negozi, ristoranti con delivery, prodotti da asporto
  // --------------------------------------------------------------------------
  ecommerce: {
    id: 'ecommerce',
    name: 'E-Commerce',
    description: 'Vendita prodotti con diverse modalità di consegna',
    icon: 'ShoppingBag',
    color: 'purple',
    schema: {
      pricingType: 'fixed',
      bookingUnit: 'item',
    },
    // Sub-moduli attivabili singolarmente
    submodules: {
      products: {
        id: 'products',
        name: 'Catalogo Prodotti',
        description: 'Gestione catalogo prodotti',
        required: true, // Sempre attivo se ecommerce è attivo
      },
      shipping: {
        id: 'shipping',
        name: 'Spedizioni',
        description: 'Consegna tramite corriere',
        required: false,
        partnerSettings: {
          freeShippingThreshold: { type: 'number', default: 50, label: 'Soglia spedizione gratuita' },
          handlingTime: { type: 'number', default: 1, label: 'Tempo preparazione (giorni)' },
        },
      },
      delivery: {
        id: 'delivery',
        name: 'Delivery',
        description: 'Consegna a domicilio locale',
        required: false,
        partnerSettings: {
          deliveryRadius: { type: 'number', default: 5, label: 'Raggio consegna (km)' },
          minOrderDelivery: { type: 'number', default: 15, label: 'Ordine minimo delivery' },
          deliveryFee: { type: 'number', default: 2, label: 'Costo delivery' },
          freeDeliveryThreshold: { type: 'number', default: 30, label: 'Soglia delivery gratuito' },
          estimatedTime: { type: 'number', default: 45, label: 'Tempo stimato (minuti)' },
        },
      },
      takeaway: {
        id: 'takeaway',
        name: 'Takeaway',
        description: 'Ritiro in sede',
        required: false,
        partnerSettings: {
          preparationTime: { type: 'number', default: 15, label: 'Tempo preparazione (minuti)' },
          allowScheduled: { type: 'boolean', default: true, label: 'Consenti prenotazione orario' },
        },
      },
    },
    partnerSettings: {
      trackInventory: { type: 'boolean', default: true, label: 'Gestisci inventario' },
      allowPartialOrders: { type: 'boolean', default: false, label: 'Consenti ordini parziali' },
    },
  },

  // --------------------------------------------------------------------------
  // MODULO: PRENOTAZIONE TAVOLI
  // Per: ristoranti, bar, locali
  // --------------------------------------------------------------------------
  tables: {
    id: 'tables',
    name: 'Prenotazione Tavoli',
    description: 'Prenotazione tavoli con gestione coperti',
    icon: 'UtensilsCrossed',
    color: 'rose',
    schema: {
      requiresResource: true,
      resourceTypes: ['table', 'area'],
      pricingType: 'free',
      bookingUnit: 'table',
    },
    partnerSettings: {
      defaultDuration: { type: 'number', default: 90, label: 'Durata standard (minuti)' },
      maxGuests: { type: 'number', default: 12, label: 'Ospiti max per prenotazione' },
      requiresDeposit: { type: 'boolean', default: false, label: 'Richiedi caparra' },
      depositAmount: { type: 'number', default: 10, label: 'Importo caparra per persona' },
      allowOutdoor: { type: 'boolean', default: true, label: 'Posti all\'aperto disponibili' },
    },
  },
};

// ============================================================================
// SEZIONE 2: STATI E CONFIGURAZIONI GLOBALI
// ============================================================================

export const BOOKING_STATUSES = ['pending', 'confirmed', 'completed', 'cancelled', 'no_show'];
export const ORDER_STATUSES = ['pending', 'confirmed', 'preparing', 'ready', 'shipped', 'delivered', 'cancelled'];
export const PAYMENT_STATUSES = ['pending', 'paid', 'refunded', 'failed'];

export const PLATFORM_CONFIG = {
  name: 'Skipres',
  version: '2.0',
  currency: 'EUR',
  locale: 'it-IT',
  timezone: 'Europe/Rome',
  supportEmail: 'support@skipres.it',
};

// ============================================================================
// SEZIONE 3: PROGRAMMI FEDELTA
// ============================================================================

export const LOYALTY_PROGRAMS = {
  star: {
    id: 'star',
    name: 'Programma Star',
    description: 'Invita amici e guadagna crediti',
    isActive: true,
    referralBonus: 5,
    referralBonusReferee: 5,
    levels: [
      { level: 1, name: 'Star', minReferrals: 0, commission: 1, discount: 2, color: 'purple' },
      { level: 2, name: 'Super Star', minReferrals: 30, commission: 3, discount: 4, color: 'amber' },
      { level: 3, name: 'Mega Star', minReferrals: 100, commission: 5, discount: 6, color: 'rose' },
    ],
  },
  elitUser: {
    id: 'elitUser',
    name: 'Elit User',
    description: 'Programma fedeltà per clienti',
    isActive: true,
    levels: [
      { level: 1, name: 'Elit Base', minPurchases: 0, discount: 0, cashback: 1, color: 'emerald' },
      { level: 2, name: 'Elit Silver', minPurchases: 10, discount: 5, cashback: 2, color: 'slate' },
      { level: 3, name: 'Elit Gold', minPurchases: 30, discount: 10, cashback: 3, color: 'amber' },
      { level: 4, name: 'Elit Platinum', minPurchases: 100, discount: 15, cashback: 5, color: 'purple' },
    ],
  },
  elitPartner: {
    id: 'elitPartner',
    name: 'Elit Partner',
    description: 'Badge qualità per partner',
    isActive: true,
    requirements: {
      minRating: 4.5,
      minReviews: 50,
      responseRate: 95,
      verifiedProfile: true,
    },
    benefits: ['Badge Elit', 'Priorità ricerca', 'Commissioni ridotte', 'Supporto dedicato'],
  },
};

// ============================================================================
// SEZIONE 4: CATEGORIE
// ============================================================================

export const CATEGORY_GROUPS = [
  { id: 'bellezza', name: 'Bellezza e Cura', color: 'pink' },
  { id: 'salute', name: 'Salute', color: 'red' },
  { id: 'sport', name: 'Sport e Fitness', color: 'orange' },
  { id: 'food', name: 'Ristorazione', color: 'amber' },
  { id: 'travel', name: 'Viaggi e Alloggi', color: 'sky' },
  { id: 'transport', name: 'Trasporti', color: 'yellow' },
  { id: 'auto', name: 'Servizi Auto', color: 'slate' },
  { id: 'casa', name: 'Casa e Servizi', color: 'teal' },
  { id: 'professionisti', name: 'Professionisti', color: 'indigo' },
  { id: 'formazione', name: 'Formazione', color: 'purple' },
  { id: 'eventi', name: 'Eventi', color: 'rose' },
  { id: 'shopping', name: 'Shopping', color: 'blue' },
];

export const CATEGORIES = [
  // Bellezza
  { id: 1, name: 'Parrucchieri', slug: 'parrucchieri', group: 'bellezza', suggestedModules: ['appointments', 'ecommerce'] },
  { id: 2, name: 'Estetisti', slug: 'estetisti', group: 'bellezza', suggestedModules: ['appointments', 'ecommerce'] },
  { id: 3, name: 'Barbieri', slug: 'barbieri', group: 'bellezza', suggestedModules: ['appointments'] },
  { id: 4, name: 'Centri Benessere', slug: 'centri-benessere', group: 'bellezza', suggestedModules: ['appointments', 'ecommerce'] },
  { id: 5, name: 'Massaggiatori', slug: 'massaggiatori', group: 'bellezza', suggestedModules: ['appointments'] },
  // Salute
  { id: 10, name: 'Medici', slug: 'medici', group: 'salute', suggestedModules: ['appointments'] },
  { id: 11, name: 'Dentisti', slug: 'dentisti', group: 'salute', suggestedModules: ['appointments'] },
  { id: 12, name: 'Fisioterapisti', slug: 'fisioterapisti', group: 'salute', suggestedModules: ['appointments', 'courses'] },
  { id: 13, name: 'Psicologi', slug: 'psicologi', group: 'salute', suggestedModules: ['appointments'] },
  { id: 14, name: 'Veterinari', slug: 'veterinari', group: 'salute', suggestedModules: ['appointments', 'ecommerce'] },
  // Sport
  { id: 20, name: 'Palestre', slug: 'palestre', group: 'sport', suggestedModules: ['courses', 'appointments'] },
  { id: 21, name: 'Piscine', slug: 'piscine', group: 'sport', suggestedModules: ['courses', 'rentals'] },
  { id: 22, name: 'Campi Sportivi', slug: 'campi-sportivi', group: 'sport', suggestedModules: ['rentals'] },
  { id: 23, name: 'Personal Trainer', slug: 'personal-trainer', group: 'sport', suggestedModules: ['appointments', 'courses'] },
  { id: 24, name: 'Scuole Danza', slug: 'scuole-danza', group: 'sport', suggestedModules: ['courses'] },
  // Ristorazione
  { id: 30, name: 'Ristoranti', slug: 'ristoranti', group: 'food', suggestedModules: ['tables', 'ecommerce'] },
  { id: 31, name: 'Pizzerie', slug: 'pizzerie', group: 'food', suggestedModules: ['tables', 'ecommerce'] },
  { id: 32, name: 'Bar e Caffè', slug: 'bar-caffe', group: 'food', suggestedModules: ['ecommerce'] },
  { id: 33, name: 'Pasticcerie', slug: 'pasticcerie', group: 'food', suggestedModules: ['ecommerce'] },
  // Viaggi
  { id: 50, name: 'Hotel', slug: 'hotel', group: 'travel', suggestedModules: ['rentals', 'ecommerce'] },
  { id: 51, name: 'B&B', slug: 'bed-breakfast', group: 'travel', suggestedModules: ['rentals'] },
  { id: 52, name: 'Case Vacanza', slug: 'case-vacanza', group: 'travel', suggestedModules: ['rentals'] },
  { id: 53, name: 'Agriturismi', slug: 'agriturismi', group: 'travel', suggestedModules: ['rentals', 'tables', 'ecommerce'] },
  // Trasporti
  { id: 60, name: 'Taxi', slug: 'taxi', group: 'transport', suggestedModules: ['appointments'] },
  { id: 61, name: 'NCC', slug: 'ncc', group: 'transport', suggestedModules: ['appointments', 'rentals'] },
  { id: 62, name: 'Noleggio Auto', slug: 'noleggio-auto', group: 'transport', suggestedModules: ['rentals'] },
  { id: 63, name: 'Noleggio Barche', slug: 'noleggio-barche', group: 'transport', suggestedModules: ['rentals'] },
  // Servizi Auto
  { id: 70, name: 'Meccanici', slug: 'meccanici', group: 'auto', suggestedModules: ['appointments'] },
  { id: 71, name: 'Carrozzerie', slug: 'carrozzerie', group: 'auto', suggestedModules: ['appointments'] },
  { id: 72, name: 'Autolavaggi', slug: 'autolavaggi', group: 'auto', suggestedModules: ['appointments'] },
  // Casa
  { id: 80, name: 'Idraulici', slug: 'idraulici', group: 'casa', suggestedModules: ['appointments'] },
  { id: 81, name: 'Elettricisti', slug: 'elettricisti', group: 'casa', suggestedModules: ['appointments'] },
  { id: 82, name: 'Pulizie', slug: 'pulizie', group: 'casa', suggestedModules: ['appointments'] },
  // Professionisti
  { id: 100, name: 'Avvocati', slug: 'avvocati', group: 'professionisti', suggestedModules: ['appointments'] },
  { id: 101, name: 'Commercialisti', slug: 'commercialisti', group: 'professionisti', suggestedModules: ['appointments'] },
  { id: 102, name: 'Architetti', slug: 'architetti', group: 'professionisti', suggestedModules: ['appointments'] },
  // Formazione
  { id: 110, name: 'Scuole Guida', slug: 'scuole-guida', group: 'formazione', suggestedModules: ['courses', 'appointments'] },
  { id: 111, name: 'Scuole Lingue', slug: 'scuole-lingue', group: 'formazione', suggestedModules: ['courses'] },
  { id: 112, name: 'Scuole Musica', slug: 'scuole-musica', group: 'formazione', suggestedModules: ['courses', 'appointments'] },
  // Eventi
  { id: 120, name: 'Fotografi', slug: 'fotografi', group: 'eventi', suggestedModules: ['appointments', 'ecommerce'] },
  { id: 121, name: 'Sale Ricevimenti', slug: 'sale-ricevimenti', group: 'eventi', suggestedModules: ['events', 'rentals'] },
  { id: 122, name: 'Wedding Planner', slug: 'wedding-planner', group: 'eventi', suggestedModules: ['appointments', 'events'] },
  // Shopping
  { id: 140, name: 'Abbigliamento', slug: 'abbigliamento', group: 'shopping', suggestedModules: ['ecommerce'] },
  { id: 141, name: 'Gioiellerie', slug: 'gioiellerie', group: 'shopping', suggestedModules: ['ecommerce', 'appointments'] },
  { id: 142, name: 'Elettronica', slug: 'elettronica', group: 'shopping', suggestedModules: ['ecommerce'] },
];

// ============================================================================
// SEZIONE 5: AZIENDE (COMPANIES)
// ============================================================================

export const COMPANIES = [
  { id: 1, name: 'Errico Modacapelli', slug: 'errico-modacapelli', categorySlug: 'parrucchieri', isElit: true },
  { id: 2, name: 'Civico Barber Shop', slug: 'civico-barber-shop', categorySlug: 'barbieri', isElit: false },
  { id: 3, name: 'Beauty Wellness Spa', slug: 'beauty-wellness-spa', categorySlug: 'centri-benessere', isElit: true },
  { id: 4, name: 'Fit Zone', slug: 'fit-zone', categorySlug: 'palestre', isElit: false },
  { id: 5, name: 'La Terrazza', slug: 'la-terrazza', categorySlug: 'ristoranti', isElit: true },
  { id: 6, name: 'Studio Dentistico Sorriso', slug: 'studio-sorriso', categorySlug: 'dentisti', isElit: false },
  { id: 7, name: 'Napoli Taxi Service', slug: 'napoli-taxi', categorySlug: 'taxi', isElit: true },
  { id: 8, name: 'Grand Hotel Vesuvio', slug: 'grand-hotel-vesuvio', categorySlug: 'hotel', isElit: true },
  { id: 9, name: 'B&B Centro Storico', slug: 'bb-centro-storico', categorySlug: 'bed-breakfast', isElit: false },
  { id: 10, name: 'Easy Rent Napoli', slug: 'easy-rent-napoli', categorySlug: 'noleggio-auto', isElit: true },
  { id: 11, name: 'Pizzeria Da Michele', slug: 'pizzeria-da-michele', categorySlug: 'pizzerie', isElit: false },
  { id: 12, name: 'Moda Boutique', slug: 'moda-boutique', categorySlug: 'abbigliamento', isElit: false },
  // Nuove aziende bio per La Biografica
  { id: 13, name: 'Apicoltura Vesuvio', slug: 'apicoltura-vesuvio', categorySlug: 'alimentari-bio', isElit: true },
];

// ============================================================================
// SEZIONE 6: SEDI (OFFICES)
// ============================================================================

export const OFFICES = [
  {
    id: 1,
    companyId: 1,
    name: 'Errico Modacapelli - S.G. Vesuviano',
    slug: 'errico-modacapelli-sgv',
    categorySlug: 'parrucchieri',
    address: 'Corso Vittorio Emanuele 28, San Giuseppe Vesuviano (NA)',
    phone: '+39 081 123 4567',
    email: 'info@erricomodacapelli.it',
    coordinates: { lat: 40.8518, lng: 14.2681 },
    distance: '2.3 km',
    rating: 4.9,
    reviewsCount: 127,
    isElit: true,
    image: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=800',
    hours: {
      mon: null, // Chiuso
      tue: { open: '08:30', close: '20:00' },
      wed: { open: '08:30', close: '20:00' },
      thu: { open: '09:00', close: '18:00' },
      fri: { open: '08:30', close: '20:00' },
      sat: { open: '08:30', close: '20:00' },
      sun: null,
    },
    // CONFIGURAZIONE MODULARE
    enabledModules: ['appointments', 'ecommerce'],
    enabledSubmodules: { ecommerce: ['products', 'shipping'] },
    moduleSettings: {
      appointments: {
        advanceBookingDays: 30,
        minBookingNotice: 60,
        requiresConfirmation: false,
        allowCancellation: true,
        cancellationNotice: 24,
      },
      ecommerce: {
        trackInventory: true,
        shipping: {
          freeShippingThreshold: 50,
          handlingTime: 2,
          zones: [
            { id: 'local', name: 'Locale', price: 4.99, minDays: 1, maxDays: 2 },
            { id: 'national', name: 'Italia', price: 7.99, minDays: 3, maxDays: 5 },
          ],
        },
      },
    },
  },
  {
    id: 2,
    companyId: 1,
    name: 'Errico Modacapelli - Napoli Centro',
    slug: 'errico-modacapelli-napoli',
    categorySlug: 'parrucchieri',
    address: 'Via Toledo 45, Napoli',
    phone: '+39 081 234 5678',
    email: 'napoli@erricomodacapelli.it',
    coordinates: { lat: 40.8396, lng: 14.2488 },
    distance: '1.2 km',
    rating: 4.8,
    reviewsCount: 95,
    isElit: true,
    image: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=800',
    hours: {
      mon: { open: '09:00', close: '19:00' },
      tue: { open: '09:00', close: '19:00' },
      wed: { open: '09:00', close: '19:00' },
      thu: { open: '09:00', close: '19:00' },
      fri: { open: '09:00', close: '19:00' },
      sat: { open: '09:00', close: '18:00' },
      sun: null,
    },
    enabledModules: ['appointments', 'ecommerce'],
    enabledSubmodules: { ecommerce: ['products', 'shipping'] },
    moduleSettings: {
      appointments: {
        advanceBookingDays: 30,
        minBookingNotice: 60,
        requiresConfirmation: false,
      },
    },
  },
  {
    id: 3,
    companyId: 4,
    name: 'Fit Zone - Palestra',
    slug: 'fit-zone',
    categorySlug: 'palestre',
    address: 'Via Roma 100, Napoli',
    phone: '+39 081 345 6789',
    coordinates: { lat: 40.8500, lng: 14.2600 },
    distance: '0.8 km',
    rating: 4.6,
    reviewsCount: 89,
    isElit: false,
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800',
    hours: {
      mon: { open: '06:00', close: '23:00' },
      tue: { open: '06:00', close: '23:00' },
      wed: { open: '06:00', close: '23:00' },
      thu: { open: '06:00', close: '23:00' },
      fri: { open: '06:00', close: '23:00' },
      sat: { open: '08:00', close: '20:00' },
      sun: { open: '09:00', close: '14:00' },
    },
    enabledModules: ['courses', 'appointments', 'events'],
    enabledSubmodules: {},
    moduleSettings: {
      courses: {
        allowTrialLesson: true,
        trialPrice: 0,
        maxParticipants: 25,
        allowDropIn: true,
        dropInPrice: 12,
      },
    },
  },
  {
    id: 4,
    companyId: 5,
    name: 'La Terrazza Ristorante',
    slug: 'la-terrazza',
    categorySlug: 'ristoranti',
    address: 'Lungomare Caracciolo 50, Napoli',
    phone: '+39 081 456 7890',
    coordinates: { lat: 40.8300, lng: 14.2400 },
    distance: '1.5 km',
    rating: 4.7,
    reviewsCount: 234,
    isElit: true,
    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800',
    hours: {
      mon: null,
      tue: { open: '12:30', close: '15:00', dinner: { open: '19:30', close: '23:00' } },
      wed: { open: '12:30', close: '15:00', dinner: { open: '19:30', close: '23:00' } },
      thu: { open: '12:30', close: '15:00', dinner: { open: '19:30', close: '23:00' } },
      fri: { open: '12:30', close: '15:00', dinner: { open: '19:30', close: '23:30' } },
      sat: { open: '12:30', close: '15:30', dinner: { open: '19:30', close: '23:30' } },
      sun: { open: '12:30', close: '16:00' },
    },
    enabledModules: ['tables', 'ecommerce'],
    enabledSubmodules: { ecommerce: ['products', 'delivery', 'takeaway'] },
    moduleSettings: {
      tables: {
        defaultDuration: 120,
        maxGuests: 10,
        requiresDeposit: true,
        depositAmount: 20,
        allowOutdoor: true,
      },
      ecommerce: {
        delivery: {
          deliveryRadius: 8,
          minOrderDelivery: 20,
          deliveryFee: 3,
          freeDeliveryThreshold: 40,
          estimatedTime: 45,
        },
        takeaway: {
          preparationTime: 20,
          allowScheduled: true,
        },
      },
    },
  },
  {
    id: 5,
    companyId: 8,
    name: 'Grand Hotel Vesuvio',
    slug: 'grand-hotel-vesuvio',
    categorySlug: 'hotel',
    address: 'Via Partenope 45, Napoli',
    phone: '+39 081 567 8901',
    coordinates: { lat: 40.8280, lng: 14.2450 },
    distance: '2.1 km',
    rating: 4.9,
    reviewsCount: 312,
    isElit: true,
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=800',
    hours: { reception: '24h' },
    enabledModules: ['rentals', 'ecommerce'],
    enabledSubmodules: { ecommerce: ['products'] },
    moduleSettings: {
      rentals: {
        minStay: 1,
        maxStay: 30,
        checkInTime: '15:00',
        checkOutTime: '11:00',
        weekendMarkup: 15,
        depositRequired: true,
        depositPercent: 30,
      },
    },
  },
  {
    id: 6,
    companyId: 10,
    name: 'Easy Rent Napoli',
    slug: 'easy-rent-napoli',
    categorySlug: 'noleggio-auto',
    address: 'Piazza Garibaldi 20, Napoli',
    phone: '+39 081 678 9012',
    coordinates: { lat: 40.8530, lng: 14.2720 },
    distance: '3.5 km',
    rating: 4.5,
    reviewsCount: 78,
    isElit: true,
    image: 'https://images.unsplash.com/photo-1449965408869-eaa3f722e40d?w=800',
    hours: {
      mon: { open: '08:00', close: '20:00' },
      tue: { open: '08:00', close: '20:00' },
      wed: { open: '08:00', close: '20:00' },
      thu: { open: '08:00', close: '20:00' },
      fri: { open: '08:00', close: '20:00' },
      sat: { open: '08:00', close: '18:00' },
      sun: { open: '09:00', close: '13:00' },
    },
    enabledModules: ['rentals'],
    enabledSubmodules: {},
    moduleSettings: {
      rentals: {
        minStay: 1,
        maxStay: 90,
        checkInTime: '08:00',
        checkOutTime: '20:00',
        weekendMarkup: 10,
        depositRequired: true,
        depositPercent: 20,
      },
    },
  },
  {
    id: 7,
    companyId: 11,
    name: 'Pizzeria Da Michele',
    slug: 'pizzeria-da-michele',
    categorySlug: 'pizzerie',
    address: 'Via Cesare Sersale 1, Napoli',
    phone: '+39 081 553 9204',
    coordinates: { lat: 40.8500, lng: 14.2650 },
    distance: '1.8 km',
    rating: 4.8,
    reviewsCount: 1245,
    isElit: false,
    image: 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=800',
    hours: {
      mon: { open: '11:00', close: '23:00' },
      tue: { open: '11:00', close: '23:00' },
      wed: { open: '11:00', close: '23:00' },
      thu: { open: '11:00', close: '23:00' },
      fri: { open: '11:00', close: '24:00' },
      sat: { open: '11:00', close: '24:00' },
      sun: null,
    },
    enabledModules: ['ecommerce'],
    enabledSubmodules: { ecommerce: ['products', 'delivery', 'takeaway'] },
    moduleSettings: {
      ecommerce: {
        delivery: {
          deliveryRadius: 5,
          minOrderDelivery: 15,
          deliveryFee: 2,
          freeDeliveryThreshold: 25,
          estimatedTime: 30,
        },
        takeaway: {
          preparationTime: 15,
          allowScheduled: true,
        },
      },
    },
  },
  {
    id: 8,
    companyId: 12,
    name: 'Moda Boutique',
    slug: 'moda-boutique',
    categorySlug: 'abbigliamento',
    address: 'Via Chiaia 120, Napoli',
    phone: '+39 081 789 0123',
    coordinates: { lat: 40.8370, lng: 14.2430 },
    distance: '2.0 km',
    rating: 4.4,
    reviewsCount: 56,
    isElit: false,
    image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=800',
    hours: {
      mon: { open: '10:00', close: '13:30', afternoon: { open: '16:00', close: '20:00' } },
      tue: { open: '10:00', close: '13:30', afternoon: { open: '16:00', close: '20:00' } },
      wed: { open: '10:00', close: '13:30', afternoon: { open: '16:00', close: '20:00' } },
      thu: { open: '10:00', close: '13:30', afternoon: { open: '16:00', close: '20:00' } },
      fri: { open: '10:00', close: '13:30', afternoon: { open: '16:00', close: '20:00' } },
      sat: { open: '10:00', close: '20:00' },
      sun: null,
    },
    enabledModules: ['ecommerce'],
    enabledSubmodules: { ecommerce: ['products', 'shipping'] },
    moduleSettings: {
      ecommerce: {
        trackInventory: true,
        shipping: {
          freeShippingThreshold: 100,
          handlingTime: 1,
          zones: [
            { id: 'local', name: 'Campania', price: 5.99, minDays: 1, maxDays: 2 },
            { id: 'national', name: 'Italia', price: 8.99, minDays: 2, maxDays: 4 },
            { id: 'europe', name: 'Europa', price: 15.99, minDays: 5, maxDays: 10 },
          ],
        },
      },
    },
  },
  // --------------------------------------------------------------------------
  // OFFICES per Companies precedentemente orfane + nuovi tipi risorsa
  // --------------------------------------------------------------------------
  {
    id: 9,
    companyId: 7,
    name: 'Napoli Boat Charter',
    slug: 'napoli-boat-charter',
    categorySlug: 'noleggio-barche',
    address: 'Porto di Mergellina, Napoli',
    phone: '+39 081 890 1234',
    email: 'info@napoliboat.it',
    coordinates: { lat: 40.8280, lng: 14.2200 },
    distance: '4.2 km',
    rating: 4.7,
    reviewsCount: 89,
    isElit: true,
    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800',
    hours: {
      mon: { open: '08:00', close: '19:00' },
      tue: { open: '08:00', close: '19:00' },
      wed: { open: '08:00', close: '19:00' },
      thu: { open: '08:00', close: '19:00' },
      fri: { open: '08:00', close: '19:00' },
      sat: { open: '08:00', close: '20:00' },
      sun: { open: '09:00', close: '18:00' },
    },
    enabledModules: ['rentals'],
    enabledSubmodules: {},
    moduleSettings: {
      rentals: {
        minStay: 1,
        maxStay: 14,
        checkInTime: '09:00',
        checkOutTime: '18:00',
        weekendMarkup: 20,
        depositRequired: true,
        depositPercent: 30,
      },
    },
  },
  {
    id: 10,
    companyId: 9,
    name: 'B&B Centro Storico',
    slug: 'bb-centro-storico',
    categorySlug: 'bed-breakfast',
    address: 'Via dei Tribunali 120, Napoli',
    phone: '+39 081 901 2345',
    email: 'info@bbcentrostorico.it',
    coordinates: { lat: 40.8510, lng: 14.2570 },
    distance: '1.0 km',
    rating: 4.6,
    reviewsCount: 156,
    isElit: false,
    image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800',
    hours: { reception: '08:00-22:00' },
    enabledModules: ['rentals'],
    enabledSubmodules: {},
    moduleSettings: {
      rentals: {
        minStay: 1,
        maxStay: 30,
        checkInTime: '14:00',
        checkOutTime: '10:00',
        weekendMarkup: 10,
        depositRequired: false,
      },
    },
  },
  {
    id: 11,
    companyId: 2,
    name: 'Civico Barber Shop',
    slug: 'civico-barber-shop',
    categorySlug: 'barbieri',
    address: 'Via Civico 45, Napoli',
    phone: '+39 081 012 3456',
    email: 'info@civicobarber.it',
    coordinates: { lat: 40.8450, lng: 14.2520 },
    distance: '1.3 km',
    rating: 4.5,
    reviewsCount: 67,
    isElit: false,
    image: 'https://images.unsplash.com/photo-1585747860715-2ba37e788b70?w=800',
    hours: {
      mon: null,
      tue: { open: '09:00', close: '19:30' },
      wed: { open: '09:00', close: '19:30' },
      thu: { open: '09:00', close: '19:30' },
      fri: { open: '09:00', close: '19:30' },
      sat: { open: '08:30', close: '18:00' },
      sun: null,
    },
    enabledModules: ['appointments'],
    enabledSubmodules: {},
    moduleSettings: {
      appointments: {
        advanceBookingDays: 14,
        minBookingNotice: 30,
        requiresConfirmation: false,
      },
    },
  },
  {
    id: 12,
    companyId: 3,
    name: 'Beauty Wellness Spa',
    slug: 'beauty-wellness-spa',
    categorySlug: 'centri-benessere',
    address: 'Via Posillipo 88, Napoli',
    phone: '+39 081 123 4567',
    email: 'info@beautywellness.it',
    coordinates: { lat: 40.8100, lng: 14.1950 },
    distance: '5.2 km',
    rating: 4.9,
    reviewsCount: 203,
    isElit: true,
    image: 'https://images.unsplash.com/photo-1544161515-4ab6ce6db874?w=800',
    hours: {
      mon: { open: '10:00', close: '20:00' },
      tue: { open: '10:00', close: '20:00' },
      wed: { open: '10:00', close: '20:00' },
      thu: { open: '10:00', close: '20:00' },
      fri: { open: '10:00', close: '21:00' },
      sat: { open: '09:00', close: '19:00' },
      sun: null,
    },
    enabledModules: ['appointments', 'ecommerce', 'events'],
    enabledSubmodules: { ecommerce: ['products'] },
    moduleSettings: {
      appointments: {
        advanceBookingDays: 60,
        minBookingNotice: 120,
        requiresConfirmation: true,
        allowCancellation: true,
        cancellationNotice: 48,
      },
    },
  },
  {
    id: 13,
    companyId: 6,
    name: 'Studio Dentistico Sorriso',
    slug: 'studio-sorriso',
    categorySlug: 'dentisti',
    address: 'Via Roma 200, Napoli',
    phone: '+39 081 234 5678',
    email: 'info@studiosorriso.it',
    coordinates: { lat: 40.8480, lng: 14.2550 },
    distance: '0.5 km',
    rating: 4.8,
    reviewsCount: 178,
    isElit: false,
    image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?w=800',
    hours: {
      mon: { open: '09:00', close: '13:00', afternoon: { open: '15:00', close: '19:00' } },
      tue: { open: '09:00', close: '13:00', afternoon: { open: '15:00', close: '19:00' } },
      wed: { open: '09:00', close: '13:00', afternoon: { open: '15:00', close: '19:00' } },
      thu: { open: '09:00', close: '13:00', afternoon: { open: '15:00', close: '19:00' } },
      fri: { open: '09:00', close: '13:00' },
      sat: null,
      sun: null,
    },
    enabledModules: ['appointments'],
    enabledSubmodules: {},
    moduleSettings: {
      appointments: {
        advanceBookingDays: 90,
        minBookingNotice: 180,
        requiresConfirmation: true,
        allowCancellation: true,
        cancellationNotice: 48,
      },
    },
  },
  // --------------------------------------------------------------------------
  // APICOLTURA VESUVIO - Produttore di miele biologico (La Biografica)
  // --------------------------------------------------------------------------
  {
    id: 14,
    companyId: 13,
    name: 'Apicoltura Vesuvio',
    slug: 'apicoltura-vesuvio',
    categorySlug: 'alimentari-bio',
    address: 'Via delle Ginestre 45, Ottaviano (NA)',
    phone: '+39 081 888 1234',
    email: 'info@apicolturavesuvio.it',
    coordinates: { lat: 40.8550, lng: 14.4850 },
    distance: '12.5 km',
    rating: 4.9,
    reviewsCount: 89,
    isElit: true,
    image: 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=800',
    description: 'Apicoltura biologica alle pendici del Vesuvio. Produciamo miele artigianale da oltre 50 anni seguendo metodi tradizionali e nel pieno rispetto delle api e dell\'ambiente.',
    hours: {
      mon: { open: '09:00', close: '13:00', afternoon: { open: '15:00', close: '18:00' } },
      tue: { open: '09:00', close: '13:00', afternoon: { open: '15:00', close: '18:00' } },
      wed: { open: '09:00', close: '13:00', afternoon: { open: '15:00', close: '18:00' } },
      thu: { open: '09:00', close: '13:00', afternoon: { open: '15:00', close: '18:00' } },
      fri: { open: '09:00', close: '13:00', afternoon: { open: '15:00', close: '18:00' } },
      sat: { open: '09:00', close: '13:00' },
      sun: null,
    },
    enabledModules: ['ecommerce', 'events'],
    enabledSubmodules: { ecommerce: ['products', 'shipping', 'takeaway'] },
    moduleSettings: {
      ecommerce: {
        trackInventory: true,
        shipping: {
          freeShippingThreshold: 50,
          handlingTime: 2,
          zones: [
            { id: 'local', name: 'Campania', price: 5.99, minDays: 1, maxDays: 3 },
            { id: 'national', name: 'Italia', price: 7.99, minDays: 3, maxDays: 5 },
            { id: 'europe', name: 'Europa', price: 14.99, minDays: 7, maxDays: 14 },
          ],
        },
        takeaway: {
          preparationTime: 0,
          allowScheduled: true,
        },
      },
    },
    // Info aggiuntive per presentazione investitori
    certifications: ['Bio Certificato', 'DOP Vesuvio', 'Slow Food'],
    yearFounded: 1972,
    beehives: 150,
    annualProduction: '5000 kg',
  },
];

// ============================================================================
// SEZIONE 7: RISORSE (operatori, camere, veicoli, tavoli, barche, appartamenti, etc.)
// ============================================================================
// I tipi di risorsa disponibili sono:
// - operator: Personale che esegue servizi (parrucchieri, estetisti)
// - instructor: Istruttori per corsi e lezioni
// - room: Sale, camere, stanze prenotabili
// - table: Tavoli ristorante con coperti
// - vehicle: Veicoli a noleggio (auto, moto)
// - boat: Imbarcazioni a noleggio
// - apartment: Appartamenti e case vacanza
// - equipment: Attrezzature prenotabili
// - area: Zone/aree (es. terrazza, giardino)
// ============================================================================

export const RESOURCES = [
  // --------------------------------------------------------------------------
  // OPERATORI PARRUCCHIERE (Office 1 - Errico Modacapelli SGV)
  // --------------------------------------------------------------------------
  { id: 1, officeId: 1, type: 'operator', name: 'Marco', surname: 'Rossi', role: 'Senior Stylist', avatar: 'https://randomuser.me/api/portraits/men/32.jpg' },
  { id: 2, officeId: 1, type: 'operator', name: 'Sara', surname: 'Bianchi', role: 'Colorista', avatar: 'https://randomuser.me/api/portraits/women/44.jpg' },
  { id: 3, officeId: 1, type: 'operator', name: 'Luca', surname: 'Verdi', role: 'Barber', avatar: 'https://randomuser.me/api/portraits/men/67.jpg' },
  
  // --------------------------------------------------------------------------
  // OPERATORI PARRUCCHIERE (Office 2 - Errico Modacapelli Napoli)
  // --------------------------------------------------------------------------
  { id: 4, officeId: 2, type: 'operator', name: 'Alessia', surname: 'Milano', role: 'Senior Stylist', avatar: 'https://randomuser.me/api/portraits/women/25.jpg' },
  { id: 5, officeId: 2, type: 'operator', name: 'Roberto', surname: 'Centro', role: 'Colorista', avatar: 'https://randomuser.me/api/portraits/men/35.jpg' },
  
  // --------------------------------------------------------------------------
  // ISTRUTTORI E SALE PALESTRA (Office 3 - Fit Zone)
  // --------------------------------------------------------------------------
  { id: 10, officeId: 3, type: 'instructor', name: 'Paolo', surname: 'Fitness', role: 'Personal Trainer', avatar: 'https://randomuser.me/api/portraits/men/45.jpg' },
  { id: 11, officeId: 3, type: 'instructor', name: 'Giulia', surname: 'Yoga', role: 'Istruttrice Yoga', avatar: 'https://randomuser.me/api/portraits/women/33.jpg' },
  { id: 12, officeId: 3, type: 'room', name: 'Sala Corsi A', capacity: 30 },
  { id: 13, officeId: 3, type: 'room', name: 'Sala Corsi B', capacity: 20 },
  // Attrezzature palestra
  { id: 14, officeId: 3, type: 'equipment', name: 'Tapis Roulant Pro', category: 'cardio', quantity: 5 },
  { id: 15, officeId: 3, type: 'equipment', name: 'Spin Bike', category: 'cardio', quantity: 20 },
  
  // --------------------------------------------------------------------------
  // TAVOLI E AREE RISTORANTE (Office 4 - La Terrazza)
  // --------------------------------------------------------------------------
  { id: 20, officeId: 4, type: 'table', name: 'Tavolo 1', seats: 4, area: 'interno' },
  { id: 21, officeId: 4, type: 'table', name: 'Tavolo 2', seats: 4, area: 'interno' },
  { id: 22, officeId: 4, type: 'table', name: 'Tavolo 3', seats: 6, area: 'interno' },
  { id: 23, officeId: 4, type: 'table', name: 'Terrazza 1', seats: 4, area: 'esterno' },
  { id: 24, officeId: 4, type: 'table', name: 'Terrazza 2', seats: 6, area: 'esterno' },
  // Aree prenotabili
  { id: 25, officeId: 4, type: 'area', name: 'Terrazza Panoramica', capacity: 30, features: ['vista mare', 'riscaldata'] },
  { id: 26, officeId: 4, type: 'area', name: 'Sala Privata', capacity: 20, features: ['karaoke', 'schermo'] },
  
  // --------------------------------------------------------------------------
  // CAMERE HOTEL (Office 5 - Grand Hotel Vesuvio)
  // --------------------------------------------------------------------------
  { id: 30, officeId: 5, type: 'room', name: 'Camera Standard', basePrice: 120, maxGuests: 2 },
  { id: 31, officeId: 5, type: 'room', name: 'Camera Superior', basePrice: 180, maxGuests: 2 },
  { id: 32, officeId: 5, type: 'room', name: 'Suite Junior', basePrice: 280, maxGuests: 3 },
  { id: 33, officeId: 5, type: 'room', name: 'Suite Presidenziale', basePrice: 500, maxGuests: 4 },
  
  // --------------------------------------------------------------------------
  // VEICOLI NOLEGGIO (Office 6 - Easy Rent Napoli)
  // --------------------------------------------------------------------------
  { id: 40, officeId: 6, type: 'vehicle', name: 'Fiat 500', category: 'economy', basePrice: 35, image: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=400' },
  { id: 41, officeId: 6, type: 'vehicle', name: 'Fiat Panda', category: 'economy', basePrice: 30, image: 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400' },
  { id: 42, officeId: 6, type: 'vehicle', name: 'Alfa Romeo Giulia', category: 'premium', basePrice: 85, image: 'https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=400' },
  { id: 43, officeId: 6, type: 'vehicle', name: 'Vespa 125', category: 'scooter', basePrice: 25, image: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400' },
  
  // --------------------------------------------------------------------------
  // BARCHE NOLEGGIO (Office 9 - Napoli Boat Charter)
  // --------------------------------------------------------------------------
  { id: 50, officeId: 9, type: 'boat', name: 'Gozzo Tradizionale', category: 'classic', basePrice: 150, capacity: 8, image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=400' },
  { id: 51, officeId: 9, type: 'boat', name: 'Gommone 6m', category: 'sport', basePrice: 120, capacity: 6, image: 'https://images.unsplash.com/photo-1569263979104-865ab7cd8d13?w=400' },
  { id: 52, officeId: 9, type: 'boat', name: 'Barca a Vela 10m', category: 'sail', basePrice: 280, capacity: 6, image: 'https://images.unsplash.com/photo-1540946485063-a40da27545f8?w=400' },
  { id: 53, officeId: 9, type: 'boat', name: 'Yacht Luxury', category: 'luxury', basePrice: 800, capacity: 12, image: 'https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=400' },
  
  // --------------------------------------------------------------------------
  // APPARTAMENTI (Office 10 - B&B Centro Storico)
  // --------------------------------------------------------------------------
  { id: 60, officeId: 10, type: 'apartment', name: 'Monolocale Centro', basePrice: 70, maxGuests: 2, sqm: 35, image: 'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=400' },
  { id: 61, officeId: 10, type: 'apartment', name: 'Bilocale Vista Mare', basePrice: 110, maxGuests: 4, sqm: 55, image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=400' },
  { id: 62, officeId: 10, type: 'apartment', name: 'Loft Artistico', basePrice: 95, maxGuests: 3, sqm: 45, image: 'https://images.unsplash.com/photo-1536376072261-38c75010e6c9?w=400' },
  
  // --------------------------------------------------------------------------
  // OPERATORI BARBIERE (Office 11 - Civico Barber Shop)
  // --------------------------------------------------------------------------
  { id: 70, officeId: 11, type: 'operator', name: 'Antonio', surname: 'Civico', role: 'Master Barber', avatar: 'https://randomuser.me/api/portraits/men/52.jpg' },
  { id: 71, officeId: 11, type: 'operator', name: 'Salvatore', surname: 'Napoli', role: 'Barber', avatar: 'https://randomuser.me/api/portraits/men/41.jpg' },
  
  // --------------------------------------------------------------------------
  // OPERATORI SPA (Office 12 - Beauty Wellness Spa)
  // --------------------------------------------------------------------------
  { id: 80, officeId: 12, type: 'operator', name: 'Francesca', surname: 'Benessere', role: 'Estetista Senior', avatar: 'https://randomuser.me/api/portraits/women/55.jpg' },
  { id: 81, officeId: 12, type: 'operator', name: 'Chiara', surname: 'Wellness', role: 'Massaggiatrice', avatar: 'https://randomuser.me/api/portraits/women/62.jpg' },
  { id: 82, officeId: 12, type: 'operator', name: 'Elena', surname: 'Spa', role: 'Manicurista', avatar: 'https://randomuser.me/api/portraits/women/38.jpg' },
  { id: 83, officeId: 12, type: 'room', name: 'Sala Massaggi 1', capacity: 1 },
  { id: 84, officeId: 12, type: 'room', name: 'Sala Massaggi 2', capacity: 1 },
  { id: 85, officeId: 12, type: 'room', name: 'Cabina Trattamenti Viso', capacity: 1 },
  
  // --------------------------------------------------------------------------
  // OPERATORI DENTISTA (Office 13 - Studio Dentistico Sorriso)
  // --------------------------------------------------------------------------
  { id: 90, officeId: 13, type: 'operator', name: 'Dott. Marco', surname: 'Sorriso', role: 'Odontoiatra', avatar: 'https://randomuser.me/api/portraits/men/60.jpg' },
  { id: 91, officeId: 13, type: 'operator', name: 'Dott.ssa Anna', surname: 'Dentini', role: 'Ortodontista', avatar: 'https://randomuser.me/api/portraits/women/48.jpg' },
  { id: 92, officeId: 13, type: 'operator', name: 'Maria', surname: 'Igiene', role: 'Igienista Dentale', avatar: 'https://randomuser.me/api/portraits/women/29.jpg' },
  { id: 93, officeId: 13, type: 'room', name: 'Studio 1', capacity: 1 },
  { id: 94, officeId: 13, type: 'room', name: 'Studio 2', capacity: 1 },
  { id: 95, officeId: 13, type: 'equipment', name: 'Panoramica Dentale', category: 'diagnostica', quantity: 1 },
];

// ============================================================================
// SEZIONE 8: SERVIZI (per modulo appointments)
// ============================================================================

export const SERVICES = [
  // Parrucchiere (Office 1 - Errico Modacapelli SGV)
  { id: 1, officeId: 1, name: 'Taglio Uomo', price: 18, duration: 30, category: 'taglio' },
  { id: 2, officeId: 1, name: 'Taglio Donna', price: 25, duration: 45, category: 'taglio' },
  { id: 3, officeId: 1, name: 'Colore', price: 45, duration: 90, category: 'colore' },
  { id: 4, officeId: 1, name: 'Piega', price: 20, duration: 30, category: 'styling' },
  { id: 5, officeId: 1, name: 'Trattamento VIP', price: 65, duration: 60, category: 'trattamento' },
  { id: 6, officeId: 1, name: 'Colore + Taglio', price: 65, duration: 120, category: 'combo' },
  // Parrucchiere sede 2 (Office 2 - Errico Modacapelli Napoli)
  { id: 10, officeId: 2, name: 'Taglio Uomo', price: 20, duration: 30, category: 'taglio' },
  { id: 11, officeId: 2, name: 'Taglio Donna', price: 28, duration: 45, category: 'taglio' },
  { id: 12, officeId: 2, name: 'Colore', price: 50, duration: 90, category: 'colore' },
  // Palestra (Office 3 - Fit Zone) - Sessioni PT individuali
  { id: 15, officeId: 3, name: 'Personal Training 1h', price: 45, duration: 60, category: 'training' },
  { id: 16, officeId: 3, name: 'Valutazione Funzionale', price: 60, duration: 90, category: 'valutazione' },
  { id: 17, officeId: 3, name: 'Sessione Yoga Privata', price: 50, duration: 60, category: 'yoga' },
  // Barbiere (Office 11 - Civico Barber Shop)
  { id: 20, officeId: 11, name: 'Taglio Classico', price: 15, duration: 25, category: 'taglio' },
  { id: 21, officeId: 11, name: 'Taglio + Barba', price: 25, duration: 40, category: 'combo' },
  { id: 22, officeId: 11, name: 'Rasatura Tradizionale', price: 18, duration: 30, category: 'barba' },
  { id: 23, officeId: 11, name: 'Trattamento Cuoio Capelluto', price: 30, duration: 20, category: 'trattamento' },
  // Spa (Office 12 - Beauty Wellness Spa)
  { id: 30, officeId: 12, name: 'Massaggio Rilassante', price: 60, duration: 60, category: 'massaggio' },
  { id: 31, officeId: 12, name: 'Massaggio Decontratturante', price: 75, duration: 60, category: 'massaggio' },
  { id: 32, officeId: 12, name: 'Trattamento Viso Anti-Age', price: 85, duration: 75, category: 'viso' },
  { id: 33, officeId: 12, name: 'Percorso Benessere', price: 120, duration: 120, category: 'percorso' },
  { id: 34, officeId: 12, name: 'Manicure Spa', price: 35, duration: 45, category: 'mani' },
  // Dentista (Office 13 - Studio Dentistico Sorriso)
  { id: 40, officeId: 13, name: 'Visita di Controllo', price: 50, duration: 30, category: 'visita' },
  { id: 41, officeId: 13, name: 'Pulizia Dentale', price: 80, duration: 45, category: 'igiene' },
  { id: 42, officeId: 13, name: 'Sbiancamento', price: 250, duration: 90, category: 'estetica' },
  { id: 43, officeId: 13, name: 'Otturazione', price: 100, duration: 45, category: 'cura' },
  { id: 44, officeId: 13, name: 'Consulto Ortodonzia', price: 80, duration: 45, category: 'consulto' },
];

// ============================================================================
// SEZIONE 8B: OFFERTE A PRENOTAZIONE GIORNALIERA (per modulo rentals)
// ============================================================================
// Distinte dai SERVICES (orari), queste sono offerte per noleggi/soggiorni
// con prenotazione per giorno/periodo (check-in/check-out)
// ============================================================================

export const RENTALS = [
  // Hotel (Office 5 - Grand Hotel Vesuvio)
  { 
    id: 1, 
    officeId: 5, 
    resourceId: 30, // Camera Deluxe
    name: 'Camera Deluxe Vista Mare', 
    description: 'Camera elegante con vista panoramica sul golfo di Napoli',
    pricePerNight: 180, 
    minStay: 1, 
    maxStay: 14,
    maxGuests: 2,
    amenities: ['Wi-Fi', 'Colazione', 'Minibar', 'Vista Mare'],
    images: ['https://images.unsplash.com/photo-1631049307264-da0ec9d70304?w=800'],
    category: 'camera'
  },
  { 
    id: 2, 
    officeId: 5, 
    resourceId: 31, // Suite
    name: 'Suite Presidenziale', 
    description: 'Suite lussuosa con terrazza privata e jacuzzi',
    pricePerNight: 450, 
    minStay: 2, 
    maxStay: 14,
    maxGuests: 4,
    amenities: ['Wi-Fi', 'Colazione', 'Minibar', 'Jacuzzi', 'Terrazza', 'Butler'],
    images: ['https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?w=800'],
    category: 'suite'
  },
  { 
    id: 3, 
    officeId: 5, 
    resourceId: 32, // Camera Standard
    name: 'Camera Standard', 
    description: 'Camera confortevole per soggiorni brevi',
    pricePerNight: 120, 
    minStay: 1, 
    maxStay: 30,
    maxGuests: 2,
    amenities: ['Wi-Fi', 'Aria Condizionata'],
    images: ['https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=800'],
    category: 'camera'
  },
  
  // Noleggio Auto (Office 6 - AutoNoleggio Express)
  { 
    id: 10, 
    officeId: 6, 
    resourceId: 40, // Fiat 500
    name: 'Fiat 500 City', 
    description: 'Ideale per muoversi in città',
    pricePerDay: 35, 
    minDays: 1, 
    maxDays: 30,
    category: 'economy',
    features: ['A/C', 'Radio', '4 posti', 'Benzina'],
    images: ['https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=800']
  },
  { 
    id: 11, 
    officeId: 6, 
    resourceId: 41, // Alfa Romeo Giulia
    name: 'Alfa Romeo Giulia', 
    description: 'Eleganza e prestazioni',
    pricePerDay: 85, 
    minDays: 1, 
    maxDays: 30,
    category: 'premium',
    features: ['A/C', 'Navigatore', '5 posti', 'Diesel', 'Cruise Control'],
    images: ['https://images.unsplash.com/photo-1555215695-3004980ad54e?w=800']
  },
  { 
    id: 12, 
    officeId: 6, 
    resourceId: 42, // Jeep Renegade
    name: 'Jeep Renegade 4x4', 
    description: 'Perfetta per escursioni e avventure',
    pricePerDay: 75, 
    minDays: 2, 
    maxDays: 14,
    category: 'suv',
    features: ['A/C', 'Navigatore', '5 posti', '4x4', 'Diesel'],
    images: ['https://images.unsplash.com/photo-1519641471654-76ce0107ad1b?w=800']
  },
  { 
    id: 13, 
    officeId: 6, 
    resourceId: 43, // Smart ForTwo
    name: 'Smart ForTwo Electric', 
    description: 'Ecologica e agile per la città',
    pricePerDay: 30, 
    minDays: 1, 
    maxDays: 7,
    category: 'electric',
    features: ['A/C', 'Radio', '2 posti', 'Elettrica'],
    images: ['https://images.unsplash.com/photo-1593941707882-a5bba14938c7?w=800']
  },
  
  // Noleggio Barche (Office 9 - Boat Charter Amalfi)
  { 
    id: 20, 
    officeId: 9, 
    resourceId: 50, // Gozzo
    name: 'Gozzo Sorrentino Classico', 
    description: 'Barca tradizionale per scoprire la costa',
    pricePerDay: 250, 
    minDays: 1, 
    maxDays: 7,
    maxPassengers: 8,
    category: 'gozzo',
    features: ['Skipper opzionale', 'Attrezzatura snorkeling', 'Frigorifero'],
    images: ['https://images.unsplash.com/photo-1544551763-46a013bb70d5?w=800']
  },
  { 
    id: 21, 
    officeId: 9, 
    resourceId: 51, // Yacht
    name: 'Yacht Luxury 40ft', 
    description: 'Esperienza esclusiva in Costiera',
    pricePerDay: 800, 
    minDays: 1, 
    maxDays: 14,
    maxPassengers: 12,
    category: 'yacht',
    features: ['Skipper incluso', 'Cabine', 'Cucina', 'Bagno', 'Tender'],
    images: ['https://images.unsplash.com/photo-1567899378494-47b22a2ae96a?w=800']
  },
  { 
    id: 22, 
    officeId: 9, 
    resourceId: 52, // Gommone
    name: 'Gommone Joker 6mt', 
    description: 'Veloce e divertente per esplorare le grotte',
    pricePerDay: 150, 
    minDays: 1, 
    maxDays: 3,
    maxPassengers: 6,
    category: 'gommone',
    features: ['Motore 115hp', 'Tendalino', 'Scaletta'],
    images: ['https://images.unsplash.com/photo-1559827291-72ee739d0d9a?w=800']
  },
  { 
    id: 23, 
    officeId: 9, 
    resourceId: 53, // Catamarano
    name: 'Catamarano Party', 
    description: 'Ideale per gruppi e feste in mare',
    pricePerDay: 600, 
    minDays: 1, 
    maxDays: 7,
    maxPassengers: 20,
    category: 'catamarano',
    features: ['Skipper incluso', 'Sound System', 'Ampia area relax'],
    images: ['https://images.unsplash.com/photo-1540946485063-a40da27545f8?w=800']
  },
  
  // B&B / Appartamenti (Office 10 - B&B Centro Storico)
  { 
    id: 30, 
    officeId: 10, 
    resourceId: 60, // Monolocale
    name: 'Monolocale Centro', 
    description: 'Accogliente monolocale nel cuore di Napoli',
    pricePerNight: 70, 
    minStay: 2, 
    maxStay: 30,
    maxGuests: 2,
    sqm: 35,
    amenities: ['Wi-Fi', 'Cucina', 'Aria Condizionata', 'Lavatrice'],
    images: ['https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?w=800'],
    category: 'monolocale'
  },
  { 
    id: 31, 
    officeId: 10, 
    resourceId: 61, // Bilocale
    name: 'Bilocale Vista Mare', 
    description: 'Spazioso bilocale con terrazzino vista mare',
    pricePerNight: 110, 
    minStay: 2, 
    maxStay: 30,
    maxGuests: 4,
    sqm: 55,
    amenities: ['Wi-Fi', 'Cucina completa', 'Terrazzino', 'Vista Mare', 'Smart TV'],
    images: ['https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?w=800'],
    category: 'bilocale'
  },
  { 
    id: 32, 
    officeId: 10, 
    resourceId: 62, // Loft
    name: 'Loft Artistico', 
    description: 'Loft di design in palazzo storico',
    pricePerNight: 95, 
    minStay: 3, 
    maxStay: 14,
    maxGuests: 3,
    sqm: 45,
    amenities: ['Wi-Fi', 'Angolo cottura', 'Design contemporaneo'],
    images: ['https://images.unsplash.com/photo-1536376072261-38c75010e6c9?w=800'],
    category: 'loft'
  },
];

// ============================================================================
// SEZIONE 9: PRODOTTI (per modulo ecommerce)
// ============================================================================

export const PRODUCTS = [
  // Prodotti parrucchiere (Office 1 - Errico Modacapelli SGV)
  { id: 1, officeId: 1, name: 'Shampoo Professionale', price: 22, stock: 50, category: 'haircare', image: 'https://images.unsplash.com/photo-1619451334792-150fd785ee74?w=400' },
  { id: 2, officeId: 1, name: 'Maschera Nutriente', price: 28, stock: 30, category: 'haircare', image: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=400' },
  { id: 3, officeId: 1, name: 'Olio Argan Premium', price: 35, stock: 25, category: 'haircare', image: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=400' },
  { id: 4, officeId: 1, name: 'Kit Esclusivo Elit', price: 65, stock: 10, category: 'kit', image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400' },
  // Prodotti parrucchiere sede 2 (Office 2 - Errico Modacapelli Napoli)
  { id: 5, officeId: 2, name: 'Shampoo Professionale', price: 24, stock: 40, category: 'haircare', image: 'https://images.unsplash.com/photo-1608248597279-f99d160bfcbc?w=400' },
  { id: 6, officeId: 2, name: 'Balsamo Ristrutturante', price: 26, stock: 35, category: 'haircare', image: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=400' },
  { id: 7, officeId: 2, name: 'Spray Termoprotettore', price: 18, stock: 60, category: 'styling', image: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=400' },
  // Prodotti ristorante (Office 4 - La Terrazza)
  { id: 10, officeId: 4, name: 'Olio EVO Premium', price: 18, stock: 100, category: 'gourmet', image: 'https://images.unsplash.com/photo-1474979266404-7eaacbcd87c5?w=400' },
  { id: 11, officeId: 4, name: 'Vino della Casa (bottiglia)', price: 15, stock: 200, category: 'bevande', image: 'https://images.unsplash.com/photo-1510812431401-41d2bd2722f3?w=400' },
  // Hotel (Office 5 - Grand Hotel Vesuvio) - prodotti minibar/shop
  { id: 15, officeId: 5, name: 'Limoncello Artigianale', price: 25, stock: 50, category: 'bevande', image: 'https://images.unsplash.com/photo-1558346490-a72e53ae2d4f?w=400' },
  { id: 16, officeId: 5, name: 'Cofanetto Cioccolatini', price: 35, stock: 30, category: 'dolci', image: 'https://images.unsplash.com/photo-1549007994-cb92caebd54b?w=400' },
  { id: 17, officeId: 5, name: 'Kit Cortesia Premium', price: 20, stock: 100, category: 'amenities', image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400' },
  // Pizzeria (Office 7 - Pizzeria Da Michele)
  { id: 20, officeId: 7, name: 'Pizza Margherita', price: 8, stock: null, category: 'pizza', image: 'https://images.unsplash.com/photo-1574071318508-1cdbab80d002?w=400' },
  { id: 21, officeId: 7, name: 'Pizza Marinara', price: 7, stock: null, category: 'pizza', image: 'https://images.unsplash.com/photo-1565299624946-b28f40a0ae38?w=400' },
  { id: 22, officeId: 7, name: 'Pizza Diavola', price: 10, stock: null, category: 'pizza', image: 'https://images.unsplash.com/photo-1628840042765-356cda07504e?w=400' },
  { id: 23, officeId: 7, name: 'Birra Artigianale', price: 5, stock: null, category: 'bevande', image: 'https://images.unsplash.com/photo-1535958636474-b021ee887b13?w=400' },
  // Boutique (Office 8 - Moda Boutique)
  { id: 30, officeId: 8, name: 'Abito Elegante Donna', price: 189, stock: 5, category: 'abbigliamento', image: 'https://images.unsplash.com/photo-1595777457583-95e059d581b8?w=400' },
  { id: 31, officeId: 8, name: 'Camicia Uomo Slim', price: 79, stock: 12, category: 'abbigliamento', image: 'https://images.unsplash.com/photo-1620799140408-edc6dcb6d633?w=400' },
  { id: 32, officeId: 8, name: 'Borsa Pelle', price: 149, stock: 8, category: 'accessori', image: 'https://images.unsplash.com/photo-1548036328-c9fa89d128fa?w=400' },
  // Spa (Office 12 - Beauty Wellness Spa) - prodotti benessere
  { id: 40, officeId: 12, name: 'Crema Corpo Rilassante', price: 45, stock: 25, category: 'corpo', image: 'https://images.unsplash.com/photo-1608571423902-eed4a5ad8108?w=400' },
  { id: 41, officeId: 12, name: 'Olio Essenziale Lavanda', price: 22, stock: 40, category: 'aromaterapia', image: 'https://images.unsplash.com/photo-1608248543803-ba4f8c70ae0b?w=400' },
  { id: 42, officeId: 12, name: 'Set Candele Profumate', price: 38, stock: 20, category: 'ambiente', image: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400' },
  { id: 43, officeId: 12, name: 'Accappatoio Spa Luxury', price: 85, stock: 15, category: 'tessile', image: 'https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=400' },
  // --------------------------------------------------------------------------
  // APICOLTURA VESUVIO (Office 14) - Miele e prodotti delle api
  // --------------------------------------------------------------------------
  { 
    id: 50, 
    officeId: 14, 
    name: 'Miele di Castagno del Vesuvio', 
    price: 14, 
    stock: 120, 
    category: 'miele', 
    image: 'https://images.unsplash.com/photo-1692624569175-a8d226cdc3a4?w=400',
    description: 'Miele di castagno dal sapore intenso e leggermente amarognolo. Ideale per formaggi stagionati e carni.',
    weight: '500g',
    certification: 'Bio',
  },
  { 
    id: 51, 
    officeId: 14, 
    name: 'Miele Millefiori Vesuviano', 
    price: 12, 
    stock: 200, 
    category: 'miele', 
    image: 'https://images.unsplash.com/photo-1692624569328-5fbb7a0c0d5d?w=400',
    description: 'Il nostro miele più amato. Prodotto dalle api che bottinano sui fiori selvatici delle pendici del Vesuvio.',
    weight: '500g',
    certification: 'Bio',
  },
  { 
    id: 52, 
    officeId: 14, 
    name: 'Miele di Acacia Puro', 
    price: 16, 
    stock: 80, 
    category: 'miele', 
    image: 'https://images.unsplash.com/photo-1481900369621-54a7facacc6c?w=400',
    description: 'Miele delicato e cristallino, dal sapore dolce e vanigliato. Perfetto per dolci e tisane.',
    weight: '500g',
    certification: 'Bio',
  },
  { 
    id: 53, 
    officeId: 14, 
    name: 'Miele di Agrumi', 
    price: 13, 
    stock: 150, 
    category: 'miele', 
    image: 'https://images.unsplash.com/photo-1471943311424-646960669fbc?w=400',
    description: 'Profumo intenso di zagara e agrumi della costiera. Il miele della primavera campana.',
    weight: '500g',
    certification: 'Bio',
  },
  { 
    id: 54, 
    officeId: 14, 
    name: 'Propoli Puro in Gocce', 
    price: 18, 
    stock: 60, 
    category: 'propoli', 
    image: 'https://images.unsplash.com/photo-1671492248078-a4bda519c852?w=400',
    description: 'Estratto di propoli al 30%. Antibatterico naturale per il benessere delle vie respiratorie.',
    weight: '30ml',
    certification: 'Bio',
  },
  { 
    id: 55, 
    officeId: 14, 
    name: 'Pappa Reale Fresca', 
    price: 35, 
    stock: 25, 
    category: 'integratori', 
    image: 'https://images.unsplash.com/photo-1709666414115-47ecd5143293?w=400',
    description: 'Pappa reale fresca, raccolta e conservata a freddo. Energia naturale per corpo e mente.',
    weight: '25g',
    certification: 'Bio',
  },
  { 
    id: 56, 
    officeId: 14, 
    name: 'Cera d\'Api Naturale', 
    price: 8, 
    stock: 40, 
    category: 'cera', 
    image: 'https://images.unsplash.com/photo-1669938998586-491d64cf24f0?w=400',
    description: 'Cera d\'api pura per cosmetici fai-da-te, candele e lucidatura del legno.',
    weight: '200g',
    certification: 'Bio',
  },
  { 
    id: 57, 
    officeId: 14, 
    name: 'Box Degustazione 4 Mieli', 
    price: 42, 
    stock: 30, 
    category: 'gift-box', 
    image: 'https://images.unsplash.com/photo-1691480208637-6ed63aac6694?w=400',
    description: 'Cofanetto regalo con 4 vasetti da 125g: Castagno, Millefiori, Acacia e Agrumi.',
    weight: '4x125g',
    certification: 'Bio',
  },
];

// ============================================================================
// SEZIONE 10: CORSI (per modulo courses)
// ============================================================================

export const COURSES = [
  // Palestra (Office 3 - Fit Zone)
  { 
    id: 1, 
    officeId: 3, 
    name: 'Yoga Base', 
    description: 'Corso di yoga per principianti. Impara le posizioni base, tecniche di respirazione e meditazione per migliorare flessibilità e benessere mentale.',
    instructorId: 11, 
    price: 80, 
    priceType: 'monthly', 
    maxParticipants: 20,
    currentParticipants: 15,
    duration: 60,
    level: 'principiante',
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800',
    totalHours: 12,
    lessons: 12,
    startDate: '2026-03-01',
    schedule: 'Lun, Mer, Ven 09:00-10:00',
    locationType: 'in-person',
    location: 'Sala Yoga',
    city: 'Napoli',
  },
  { 
    id: 2, 
    officeId: 3, 
    name: 'Pilates', 
    description: 'Tonifica il corpo e migliora la postura con esercizi mirati. Ideale per chi cerca un allenamento a basso impatto ma efficace.',
    instructorId: 11, 
    price: 90, 
    priceType: 'monthly', 
    maxParticipants: 15,
    currentParticipants: 12,
    duration: 50,
    level: 'intermedio',
    image: 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=800',
    totalHours: 8,
    lessons: 8,
    startDate: '2026-03-01',
    schedule: 'Mar, Gio 10:00-11:00',
    locationType: 'in-person',
    location: 'Sala Pilates',
    city: 'Napoli',
  },
  { 
    id: 3, 
    officeId: 3, 
    name: 'CrossFit', 
    description: 'Allenamento ad alta intensità che combina sollevamento pesi, ginnastica e cardio. Per chi vuole spingersi al limite.',
    instructorId: 10, 
    price: 100, 
    priceType: 'monthly', 
    maxParticipants: 12,
    currentParticipants: 10,
    duration: 60,
    level: 'avanzato',
    image: 'https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800',
    totalHours: 12,
    lessons: 12,
    startDate: '2026-03-01',
    schedule: 'Lun, Mer, Ven 18:00-19:00',
    locationType: 'in-person',
    location: 'Box CrossFit',
    city: 'Napoli',
  },
  { 
    id: 4, 
    officeId: 3, 
    name: 'Spinning', 
    description: 'Allenamento cardio intenso su bici stazionaria a ritmo di musica. Brucia calorie e migliora la resistenza.',
    instructorId: 10, 
    price: 70, 
    priceType: 'monthly', 
    maxParticipants: 20,
    currentParticipants: 18,
    duration: 45,
    level: 'tutti',
    image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800',
    totalHours: 12,
    lessons: 12,
    startDate: '2026-03-01',
    schedule: 'Mar, Gio, Sab 19:00-20:00',
    locationType: 'in-person',
    location: 'Sala Spinning',
    city: 'Napoli',
  },
];

// Programmazione settimanale corsi
export const COURSE_SCHEDULE = [
  // Yoga
  { courseId: 1, dayOfWeek: 1, startTime: '09:00', endTime: '10:00', roomId: 12 }, // Lun
  { courseId: 1, dayOfWeek: 3, startTime: '09:00', endTime: '10:00', roomId: 12 }, // Mer
  { courseId: 1, dayOfWeek: 5, startTime: '18:00', endTime: '19:00', roomId: 12 }, // Ven
  // Pilates
  { courseId: 2, dayOfWeek: 2, startTime: '10:00', endTime: '11:00', roomId: 13 }, // Mar
  { courseId: 2, dayOfWeek: 4, startTime: '10:00', endTime: '11:00', roomId: 13 }, // Gio
  // CrossFit
  { courseId: 3, dayOfWeek: 1, startTime: '18:00', endTime: '19:00', roomId: 12 }, // Lun
  { courseId: 3, dayOfWeek: 3, startTime: '18:00', endTime: '19:00', roomId: 12 }, // Mer
  { courseId: 3, dayOfWeek: 5, startTime: '07:00', endTime: '08:00', roomId: 12 }, // Ven
  // Spinning
  { courseId: 4, dayOfWeek: 2, startTime: '19:00', endTime: '20:00', roomId: 13 }, // Mar
  { courseId: 4, dayOfWeek: 4, startTime: '19:00', endTime: '20:00', roomId: 13 }, // Gio
  { courseId: 4, dayOfWeek: 6, startTime: '10:00', endTime: '11:00', roomId: 13 }, // Sab
];

// ============================================================================
// SEZIONE 10B: EVENTI (per modulo events)
// ============================================================================

export const EVENTS = [
  // Spa Events (Office 12 - Beauty Wellness Spa)
  {
    id: 'evt-1',
    officeId: 12,
    name: 'Serata Benessere Completo',
    description: 'Una serata esclusiva dedicata al relax totale. Include accesso illimitato alla spa, massaggio rilassante, trattamento viso e aperitivo wellness.',
    image: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=800',
    date: '2026-03-15',
    startTime: '18:00',
    endTime: '22:00',
    price: 89,
    maxParticipants: 20,
    currentParticipants: 12,
    category: 'wellness',
    tags: ['spa', 'relax', 'massaggio'],
    status: 'active',
  },
  {
    id: 'evt-2',
    officeId: 12,
    name: 'Workshop Skincare Naturale',
    description: 'Impara a creare i tuoi cosmetici naturali con ingredienti bio. Include kit completo da portare a casa.',
    image: 'https://images.unsplash.com/photo-1556228578-0d85b1a4d571?w=800',
    date: '2026-03-22',
    startTime: '10:00',
    endTime: '13:00',
    price: 45,
    maxParticipants: 12,
    currentParticipants: 8,
    category: 'workshop',
    tags: ['skincare', 'naturale', 'diy'],
    status: 'active',
  },
  {
    id: 'evt-3',
    officeId: 12,
    name: 'Yoga & Spa Day',
    description: 'Una giornata di yoga all\'alba seguita da trattamenti spa esclusivi. Colazione bio inclusa.',
    image: 'https://images.unsplash.com/photo-1544367567-0f2fcb009e0b?w=800',
    date: '2026-04-05',
    startTime: '07:00',
    endTime: '14:00',
    price: 120,
    maxParticipants: 15,
    currentParticipants: 6,
    category: 'wellness',
    tags: ['yoga', 'spa', 'benessere'],
    status: 'active',
  },
  // Gym Events (Office 3 - Fit Zone)
  {
    id: 'evt-4',
    officeId: 3,
    name: 'Boot Camp Weekend',
    description: 'Due giorni intensivi di allenamento funzionale outdoor. Include coaching nutrizionale e piano personalizzato.',
    image: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800',
    date: '2026-03-29',
    startTime: '09:00',
    endTime: '17:00',
    price: 150,
    maxParticipants: 30,
    currentParticipants: 18,
    category: 'fitness',
    tags: ['bootcamp', 'outdoor', 'functional'],
    status: 'active',
  },
  {
    id: 'evt-5',
    officeId: 3,
    name: 'Masterclass CrossFit',
    description: 'Una giornata con atleti professionisti di CrossFit. Tecniche avanzate e competizione amichevole.',
    image: 'https://images.unsplash.com/photo-1526506118085-60ce8714f8c5?w=800',
    date: '2026-04-12',
    startTime: '10:00',
    endTime: '16:00',
    price: 75,
    maxParticipants: 25,
    currentParticipants: 20,
    category: 'fitness',
    tags: ['crossfit', 'masterclass', 'pro'],
    status: 'active',
  },
  {
    id: 'evt-6',
    officeId: 3,
    name: 'Sfida Trasformazione 30 Giorni',
    description: 'Evento di lancio della nuova sfida fitness. Incontro motivazionale, test iniziali e piano alimentare gratuito.',
    image: 'https://images.unsplash.com/photo-1517836357463-d25dfeac3438?w=800',
    date: '2026-04-01',
    startTime: '18:30',
    endTime: '21:00',
    price: 0,
    maxParticipants: 50,
    currentParticipants: 35,
    category: 'community',
    tags: ['sfida', 'trasformazione', 'gratuito'],
    status: 'active',
  },
  // --------------------------------------------------------------------------
  // EVENTI APICOLTURA VESUVIO (Office 14)
  // --------------------------------------------------------------------------
  {
    id: 'evt-7',
    officeId: 14,
    name: 'Visita Guidata all\'Apiario',
    description: 'Scopri il magico mondo delle api! Tour guidato tra le arnie, estrazione del miele dal favo e degustazione di tutti i nostri mieli biologici.',
    image: 'https://images.unsplash.com/photo-1558642452-9d2a7deb7f62?w=800',
    date: '2026-03-22',
    startTime: '10:00',
    endTime: '13:00',
    price: 25,
    maxParticipants: 15,
    currentParticipants: 8,
    category: 'experience',
    tags: ['apicoltura', 'degustazione', 'natura', 'famiglia'],
    status: 'active',
  },
  {
    id: 'evt-8',
    officeId: 14,
    name: 'Corso Base Apicoltura',
    description: 'Una giornata per imparare le basi dell\'apicoltura. Dalla biologia delle api alla gestione dell\'arnia. Include kit di partenza.',
    image: 'https://images.unsplash.com/photo-1587049352846-4a222e784d38?w=800',
    date: '2026-04-05',
    startTime: '09:00',
    endTime: '17:00',
    price: 120,
    maxParticipants: 10,
    currentParticipants: 6,
    category: 'workshop',
    tags: ['corso', 'apicoltura', 'formazione'],
    status: 'active',
  },
];

// ============================================================================
// SEZIONE 10C: COMMUNITIES (raggruppamenti tematici di Office)
// ============================================================================

export const COMMUNITIES = [
  {
    id: 'la-biografica',
    name: 'La Biografica',
    slug: 'la-biografica',
    subtitle: 'Il Naturale che fa la Differenza',
    tagline: 'Il Naturale che fa la Differenza',
    description: 'Una selezione esclusiva di aziende e professionisti che hanno scelto soluzioni naturali, biologiche e sostenibili. Dalla cosmesi ai prodotti alimentari, dalla cura del corpo al benessere olistico.',
    longDescription: 'La Biografica riunisce le eccellenze del territorio che condividono una filosofia comune: rispetto per la natura, ingredienti biologici certificati, prodotti cruelty-free e pratiche sostenibili. Scopri aziende selezionate che offrono alternative naturali senza compromessi sulla qualità.',
    logo: 'https://static.prod-images.emergentagent.com/jobs/6290848c-2fd2-4003-9140-127ab5353a0d/images/44e59bd4231dff10e923496b3376f36deae98ba779f22a8a159dae4572ec052c.png',
    coverImage: 'https://images.unsplash.com/photo-1518531933037-91b2f5f229cc?w=1200',
    // Contatti community
    phone: '+39 081 555 1234',
    email: 'info@labiografica.it',
    website: 'www.labiografica.it',
    address: 'Napoli, Campania',
    theme: {
      gradient: 'from-[#12c955] to-[#10de9a]',
      gradientFrom: '#12c955',
      gradientTo: '#10de9a',
      accent: 'green',
      primaryColor: '#12c955',
      secondaryColor: '#10de9a',
      badge: 'bg-green-100 text-green-700',
      button: 'bg-[#12c955] hover:bg-[#10de9a]',
    },
    // Office che fanno parte della community (bio, naturali, wellness)
    // 11 attività: Palestra, Spa, Parrucchieri eco, Ristorante bio, Dentista olistico, B&B eco, Boutique, Barber, Hotel wellness, Moda, APICOLTURA
    officeIds: [1, 2, 3, 4, 5, 10, 11, 12, 13, 8, 14], 
    // Categorie mostrate nella community
    categoryIds: [4, 20, 21, 22, 23, 24], // Centri Benessere, Palestre, Piscine, etc.
    // Badge e certificazioni della community
    badges: [
      { icon: 'leaf', label: 'Bio Certificato', color: 'green' },
      { icon: 'heart', label: 'Cruelty Free', color: 'pink' },
      { icon: 'recycle', label: 'Eco-Sostenibile', color: 'emerald' },
    ],
    // Statistiche
    stats: {
      offices: 2,
      products: 15,
      services: 12,
      reviews: 89,
      avgRating: 4.8,
    },
    // Featured content
    featured: {
      officeId: 12, // Beauty Wellness Spa come featured
      productIds: [40, 41, 42, 43], // Prodotti bio della spa
      serviceIds: [], // Servizi featured
    },
    // SEO e social
    seo: {
      title: 'La Biografica - Aziende Bio e Naturali | Skipres',
      description: 'Scopri la selezione di aziende certificate bio, prodotti naturali e servizi eco-sostenibili nella tua zona.',
      keywords: ['bio', 'naturale', 'biologico', 'eco', 'sostenibile', 'cruelty-free'],
    },
    // Stato
    isActive: true,
    isFeatured: true,
    createdAt: '2026-01-15',
  },
  {
    id: 'napoli-gourmet',
    name: 'Napoli Gourmet',
    slug: 'napoli-gourmet',
    subtitle: 'Il Gusto della Tradizione',
    tagline: 'Il Gusto della Tradizione',
    description: 'I migliori ristoranti, pizzerie e produttori gastronomici di Napoli. Tradizione e innovazione in tavola.',
    logo: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=400',
    coverImage: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=1200',
    phone: '+39 081 555 5678',
    email: 'info@napoligourmet.it',
    website: 'www.napoligourmet.it',
    address: 'Napoli Centro, Campania',
    theme: {
      gradient: 'from-amber-500 to-orange-500',
      accent: 'amber',
      primaryColor: '#f59e0b',
      secondaryColor: '#ea580c',
      badge: 'bg-amber-100 text-amber-700',
      button: 'bg-amber-500 hover:bg-amber-600',
    },
    officeIds: [4, 7], // La Terrazza, Pizzeria Da Michele
    categoryIds: [3, 4], // Ristoranti, Pizzerie
    badges: [
      { icon: 'utensils', label: 'Tradizione Napoletana', color: 'amber' },
      { icon: 'award', label: 'Eccellenza Culinaria', color: 'yellow' },
    ],
    stats: {
      offices: 2,
      products: 10,
      services: 0,
      reviews: 156,
      avgRating: 4.7,
    },
    isActive: true,
    isFeatured: false,
    createdAt: '2026-02-01',
  },
];

// Helper functions per le Communities
export const getCommunityById = (id) => COMMUNITIES.find(c => c.id === id || c.slug === id);
export const getCommunityOffices = (communityId) => {
  const community = getCommunityById(communityId);
  if (!community) return [];
  return OFFICES.filter(o => community.officeIds.includes(o.id));
};
export const getCommunityProducts = (communityId) => {
  const community = getCommunityById(communityId);
  if (!community) return [];
  return PRODUCTS.filter(p => community.officeIds.includes(p.officeId));
};
export const getCommunityServices = (communityId) => {
  const community = getCommunityById(communityId);
  if (!community) return [];
  return SERVICES.filter(s => community.officeIds.includes(s.officeId));
};
export const getActiveCommunities = () => COMMUNITIES.filter(c => c.isActive);
export const getFeaturedCommunities = () => COMMUNITIES.filter(c => c.isFeatured);

// ============================================================================
// SEZIONE 11: PREZZI GIORNALIERI (per modulo rentals)
// ============================================================================

export const DAILY_PRICING = {
  // Hotel (Office 5) - Camera Standard (Resource 30)
  '5-30': {
    basePrice: 120,
    weekendMarkup: 15,
    seasonalPricing: [
      { name: 'Alta Stagione', startMonth: 6, endMonth: 8, markup: 30 },
      { name: 'Natale', startMonth: 12, endMonth: 12, markup: 40 },
    ],
    customDates: {
      '2026-04-25': 180, // Festività
      '2026-05-01': 180,
      '2026-12-31': 250, // Capodanno
    },
  },
  // Noleggio auto (Office 6)
  '6-40': { basePrice: 35, weekendMarkup: 10, customDates: {} },
  '6-41': { basePrice: 30, weekendMarkup: 10, customDates: {} },
  '6-42': { basePrice: 85, weekendMarkup: 20, customDates: {} },
  '6-43': { basePrice: 25, weekendMarkup: 5, customDates: {} },
};

// ============================================================================
// SEZIONE 12: UTENTI
// ============================================================================

export const USERS = [
  // Admin
  { id: 'admin-1', email: 'admin@skipres.it', password: 'admin123', role: 'admin', firstName: 'Admin', lastName: 'Skipres' },
  // Partners
  { id: 'partner-1', email: 'errico@modacapelli.it', password: 'partner123', role: 'partner', firstName: 'Errico', lastName: 'Modacapelli', companyId: 1 },
  { id: 'partner-2', email: 'marco@terrazza.it', password: 'partner123', role: 'partner', firstName: 'Marco', lastName: 'Terrazza', companyId: 5 },
  { id: 'partner-3', email: 'anna@grandhotel.it', password: 'partner123', role: 'partner', firstName: 'Anna', lastName: 'Belvedere', companyId: 8 },
  { id: 'partner-4', email: 'paolo@fitzone.it', password: 'partner123', role: 'partner', firstName: 'Paolo', lastName: 'Fitness', companyId: 4 },
  { id: 'partner-5', email: 'luca@easyrent.it', password: 'partner123', role: 'partner', firstName: 'Luca', lastName: 'Rental', companyId: 10 },
  // Consumers
  { id: 'user-1', email: 'mario.rossi@gmail.com', password: 'password', role: 'consumer', firstName: 'Mario', lastName: 'Rossi', elitLevel: 2, starLevel: 1 },
  { id: 'user-2', email: 'giulia.bianchi@gmail.com', password: 'password', role: 'consumer', firstName: 'Giulia', lastName: 'Bianchi', elitLevel: 1 },
  { id: 'user-3', email: 'luca.verdi@gmail.com', password: 'password', role: 'consumer', firstName: 'Luca', lastName: 'Verdi', elitLevel: 3, starLevel: 2 },
];

// ============================================================================
// SEZIONE 13: PRENOTAZIONI E ORDINI (dati demo)
// ============================================================================

export const BOOKINGS = [
  // Prenotazioni servizi parrucchiere (modulo: appointments)
  { id: 'b1', officeId: 1, userId: 'user-1', moduleType: 'appointments', serviceId: 2, resourceId: 2, date: '2026-02-20', time: '10:00', duration: 45, status: 'confirmed', total: 25, notes: 'Cliente abituale' },
  { id: 'b2', officeId: 1, userId: 'user-2', moduleType: 'appointments', serviceId: 5, resourceId: 1, date: '2026-02-20', time: '14:30', duration: 60, status: 'confirmed', total: 65, notes: 'Trattamento VIP' },
  { id: 'b3', officeId: 1, userId: 'user-3', moduleType: 'appointments', serviceId: 1, resourceId: 3, date: '2026-02-21', time: '09:00', duration: 30, status: 'pending', total: 18 },
  // Prenotazioni tavolo ristorante (modulo: tables)
  { id: 'b10', officeId: 4, userId: 'user-1', moduleType: 'tables', resourceId: 23, date: '2026-02-22', time: '20:30', guests: 4, status: 'confirmed', notes: 'Tavolo terrazza' },
  { id: 'b11', officeId: 4, userId: 'user-3', moduleType: 'tables', resourceId: 24, date: '2026-02-23', time: '21:00', guests: 6, status: 'confirmed', notes: 'Compleanno' },
  // Prenotazioni camera hotel (modulo: rentals)
  { id: 'b20', officeId: 5, userId: 'user-2', moduleType: 'rentals', resourceId: 31, checkIn: '2026-03-15', checkOut: '2026-03-18', guests: 2, status: 'confirmed', total: 540, notes: '3 notti' },
  // Noleggio veicolo (modulo: rentals)
  { id: 'b30', officeId: 6, userId: 'user-1', moduleType: 'rentals', resourceId: 40, checkIn: '2026-02-25', checkOut: '2026-02-27', status: 'confirmed', total: 77, notes: '2 giorni' },
];

export const ORDERS = [
  // Ordini e-commerce parrucchiere (shipping)
  { id: 'o1', officeId: 1, userId: 'user-1', items: [{ productId: 1, quantity: 2, price: 22 }], subtotal: 44, shipping: 5.99, total: 49.99, status: 'delivered', fulfillmentType: 'shipping', date: '2026-02-10', address: 'Via Roma 10, Napoli' },
  { id: 'o2', officeId: 1, userId: 'user-2', items: [{ productId: 2, quantity: 1, price: 28 }, { productId: 3, quantity: 1, price: 35 }], subtotal: 63, shipping: 5.99, total: 68.99, status: 'shipped', fulfillmentType: 'shipping', date: '2026-02-15', address: 'Via Toledo 55, Napoli' },
  // Ordini pizzeria (delivery)
  { id: 'o10', officeId: 7, userId: 'user-1', items: [{ productId: 20, quantity: 2, price: 8 }, { productId: 23, quantity: 2, price: 5 }], subtotal: 26, delivery: 3, total: 29, status: 'delivered', fulfillmentType: 'delivery', date: '2026-02-18', address: 'Via Chiaia 100, Napoli' },
  // Ordini pizzeria (takeaway)
  { id: 'o11', officeId: 7, userId: 'user-3', items: [{ productId: 22, quantity: 1, price: 10 }, { productId: 21, quantity: 1, price: 7 }], subtotal: 17, total: 17, status: 'ready', fulfillmentType: 'takeaway', date: '2026-02-18', pickupTime: '19:30' },
  // Ordini boutique (shipping)
  { id: 'o20', officeId: 8, userId: 'user-2', items: [{ productId: 31, quantity: 1, price: 79 }], subtotal: 79, shipping: 8.99, total: 87.99, status: 'processing', fulfillmentType: 'shipping', date: '2026-02-17', address: 'Via dei Mille 25, Napoli' },
];

// ============================================================================
// SEZIONE 14: LOCATIONS (Geografia)
// ============================================================================

export const REGIONS = [
  { id: 1, name: 'Campania', slug: 'campania', countryCode: 'IT' },
  { id: 2, name: 'Lazio', slug: 'lazio', countryCode: 'IT' },
  { id: 3, name: 'Lombardia', slug: 'lombardia', countryCode: 'IT' },
];

export const CITIES = [
  { id: 1, name: 'Napoli', slug: 'napoli', regionId: 1, coordinates: { lat: 40.8518, lng: 14.2681 } },
  { id: 2, name: 'San Giuseppe Vesuviano', slug: 'san-giuseppe-vesuviano', regionId: 1, coordinates: { lat: 40.8330, lng: 14.5080 } },
  { id: 3, name: 'Roma', slug: 'roma', regionId: 2, coordinates: { lat: 41.9028, lng: 12.4964 } },
  { id: 4, name: 'Milano', slug: 'milano', regionId: 3, coordinates: { lat: 45.4642, lng: 9.1900 } },
];

export const COUNTRIES = [
  { id: 1, name: 'Italia', code: 'IT', slug: 'italia' },
];

// ============================================================================
// SEZIONE 14: RECENSIONI
// ============================================================================

export const REVIEWS = [
  // Errico Modacapelli SGV (Office 1)
  { id: 1, officeId: 1, userId: 'user-1', rating: 5, date: '2025-02-15', text: 'Servizio eccellente, personale professionale e cordiale. Consigliatissimo!' },
  { id: 2, officeId: 1, userId: 'user-2', rating: 5, date: '2025-02-10', text: 'Il migliore di Napoli! Lo consiglio a tutti.' },
  { id: 3, officeId: 1, userId: 'user-3', rating: 4, date: '2025-02-01', text: 'Ottimo servizio, prezzi nella media. Tornerò sicuramente.' },
  // Errico Modacapelli Napoli (Office 2)
  { id: 4, officeId: 2, userId: 'user-1', rating: 5, date: '2025-02-12', text: 'Sede nuova ma stesso livello di qualità. Bravissimi!' },
  { id: 5, officeId: 2, userId: 'user-3', rating: 4, date: '2025-02-05', text: 'Buon servizio, ambiente moderno e accogliente.' },
  // Fit Zone (Office 3)
  { id: 6, officeId: 3, userId: 'user-1', rating: 5, date: '2025-02-14', text: 'Palestra top! Attrezzature moderne e istruttori preparati.' },
  { id: 7, officeId: 3, userId: 'user-2', rating: 4, date: '2025-02-08', text: 'Ottimi corsi di gruppo, ambiente pulito.' },
  // La Terrazza (Office 4)
  { id: 8, officeId: 4, userId: 'user-2', rating: 5, date: '2025-02-16', text: 'Cena romantica perfetta! Vista mozzafiato e cibo delizioso.' },
  { id: 9, officeId: 4, userId: 'user-3', rating: 5, date: '2025-02-11', text: 'Miglior ristorante di Napoli, tornerò sicuramente.' },
  // Grand Hotel Vesuvio (Office 5)
  { id: 10, officeId: 5, userId: 'user-1', rating: 5, date: '2025-02-17', text: 'Hotel lussuoso, servizio impeccabile. La vista sul golfo è spettacolare!' },
  { id: 11, officeId: 5, userId: 'user-2', rating: 5, date: '2025-02-13', text: 'Esperienza indimenticabile, colazione fantastica.' },
  { id: 12, officeId: 5, userId: 'user-3', rating: 4, date: '2025-02-06', text: 'Ottimo hotel, un po\' caro ma ne vale la pena.' },
  // Easy Rent Napoli (Office 6)
  { id: 13, officeId: 6, userId: 'user-1', rating: 5, date: '2025-02-15', text: 'Noleggio veloce e auto in perfette condizioni.' },
  { id: 14, officeId: 6, userId: 'user-3', rating: 4, date: '2025-02-09', text: 'Buon servizio, prezzi competitivi.' },
  // Pizzeria Da Michele (Office 7)
  { id: 15, officeId: 7, userId: 'user-2', rating: 5, date: '2025-02-14', text: 'La vera pizza napoletana! Da provare assolutamente.' },
  { id: 16, officeId: 7, userId: 'user-1', rating: 5, date: '2025-02-07', text: 'Pizza perfetta, impasto leggero e digeribile.' },
  // Moda Boutique (Office 8)
  { id: 17, officeId: 8, userId: 'user-2', rating: 4, date: '2025-02-12', text: 'Abbigliamento di qualità, commesse gentili.' },
  // Napoli Boat Charter (Office 9)
  { id: 18, officeId: 9, userId: 'user-1', rating: 5, date: '2025-02-16', text: 'Escursione in Costiera fantastica! Skipper esperto e simpatico.' },
  { id: 19, officeId: 9, userId: 'user-3', rating: 5, date: '2025-02-10', text: 'Giornata indimenticabile, barche bellissime.' },
  // B&B Centro Storico (Office 10)
  { id: 20, officeId: 10, userId: 'user-2', rating: 4, date: '2025-02-13', text: 'Appartamento carino e ben posizionato.' },
  { id: 21, officeId: 10, userId: 'user-1', rating: 5, date: '2025-02-04', text: 'Perfetto per un weekend a Napoli!' },
  // Civico Barber Shop (Office 11)
  { id: 22, officeId: 11, userId: 'user-3', rating: 5, date: '2025-02-15', text: 'Barbiere vecchio stile, taglio perfetto!' },
  // Beauty Wellness Spa (Office 12)
  { id: 23, officeId: 12, userId: 'user-2', rating: 5, date: '2025-02-14', text: 'Massaggio rilassante top, ambiente zen.' },
  { id: 24, officeId: 12, userId: 'user-1', rating: 4, date: '2025-02-08', text: 'Trattamenti di qualità, staff professionale.' },
  // Studio Dentistico Sorriso (Office 13)
  { id: 25, officeId: 13, userId: 'user-3', rating: 5, date: '2025-02-12', text: 'Finalmente un dentista che non fa paura! Consigliatissimo.' },
  { id: 26, officeId: 13, userId: 'user-1', rating: 5, date: '2025-02-05', text: 'Studio moderno, dottori competenti e gentili.' },
];

// ============================================================================
// SEZIONE 15: NOTIFICHE
// ============================================================================

export const NOTIFICATIONS = [
  { id: 1, userId: 'user-1', type: 'booking_confirmed', title: 'Prenotazione confermata', message: 'La tua prenotazione presso Errico Modacapelli è stata confermata per domani alle 10:00', read: false, date: '2025-02-18T09:00:00' },
  { id: 2, userId: 'user-1', type: 'promo', title: 'Offerta speciale', message: 'Sconto del 20% su tutti i trattamenti viso questa settimana!', read: true, date: '2025-02-17T14:00:00' },
  { id: 3, userId: 'user-1', type: 'order_shipped', title: 'Ordine spedito', message: 'Il tuo ordine #ORD-001 è stato spedito e arriverà entro 2-3 giorni.', read: false, date: '2025-02-16T11:30:00' },
  { id: 4, userId: 'user-2', type: 'review_request', title: 'Lascia una recensione', message: 'Com\'è andata la tua esperienza da Fit Zone? Lascia una recensione!', read: false, date: '2025-02-15T16:00:00' },
  { id: 5, userId: 'user-3', type: 'booking_reminder', title: 'Promemoria appuntamento', message: 'Ricorda: hai un appuntamento domani alle 15:00 da Beauty Wellness Spa', read: false, date: '2025-02-14T18:00:00' },
  { id: 6, userId: 'partner-1', type: 'new_booking', title: 'Nuova prenotazione', message: 'Mario Rossi ha prenotato un Taglio Uomo per domani alle 10:00', read: false, date: '2025-02-18T08:30:00' },
  { id: 7, userId: 'partner-1', type: 'new_review', title: 'Nuova recensione', message: 'Hai ricevuto una nuova recensione a 5 stelle!', read: true, date: '2025-02-17T10:00:00' },
  { id: 8, userId: 'partner-3', type: 'new_booking', title: 'Nuova prenotazione camera', message: 'Giulia Bianchi ha prenotato la Suite Presidenziale per il 20-22 Febbraio', read: false, date: '2025-02-18T07:45:00' },
];

// ============================================================================
// SEZIONE 16: METODI DI PAGAMENTO
// ============================================================================

export const PAYMENT_METHODS = [
  { id: 1, userId: 'user-1', type: 'card', brand: 'visa', last4: '4242', expiry: '12/26', isDefault: true },
  { id: 2, userId: 'user-1', type: 'card', brand: 'mastercard', last4: '8888', expiry: '06/25', isDefault: false },
  { id: 3, userId: 'user-2', type: 'card', brand: 'visa', last4: '1234', expiry: '03/27', isDefault: true },
  { id: 4, userId: 'user-3', type: 'paypal', email: 'luca.verdi@gmail.com', isDefault: true },
];

// ============================================================================
// SEZIONE 17: PREFERITI
// ============================================================================

export const FAVORITES = [
  { id: 1, userId: 'user-1', itemType: 'office', itemId: 1 },
  { id: 2, userId: 'user-1', itemType: 'office', itemId: 5 },
  { id: 3, userId: 'user-1', itemType: 'service', itemId: 1 },
  { id: 4, userId: 'user-1', itemType: 'product', itemId: 1 },
  { id: 5, userId: 'user-2', itemType: 'office', itemId: 3 },
  { id: 6, userId: 'user-2', itemType: 'office', itemId: 12 },
  { id: 7, userId: 'user-3', itemType: 'rental', itemId: 20 },
  { id: 8, userId: 'user-3', itemType: 'office', itemId: 9 },
];

// ============================================================================
// SEZIONE 18: FUNZIONI HELPER
// ============================================================================

// Autenticazione
export const authenticateUser = (email, password) => {
  return USERS.find(u => u.email === email && u.password === password) || null;
};

// Getters base
export const getUserById = (id) => USERS.find(u => u.id === id);
export const getOfficeById = (id) => OFFICES.find(o => o.id === id);
export const getCompanyById = (id) => COMPANIES.find(c => c.id === id);
export const getCategoryBySlug = (slug) => CATEGORIES.find(c => c.slug === slug);

// Getters relazionali
export const getOfficesByCompanyId = (companyId) => OFFICES.filter(o => o.companyId === companyId);
export const getCompanyForOffice = (officeId) => {
  const office = getOfficeById(officeId);
  return office ? getCompanyById(office.companyId) : null;
};
export const getOfficeByOwnerId = (ownerId) => {
  const user = getUserById(ownerId);
  if (!user?.companyId) return null;
  return OFFICES.find(o => o.companyId === user.companyId);
};

// Risorse e catalogo
export const getResourcesByOfficeId = (officeId) => RESOURCES.filter(r => r.officeId === officeId);
export const getServicesByOfficeId = (officeId) => SERVICES.filter(s => s.officeId === officeId);
export const getProductsByOfficeId = (officeId) => PRODUCTS.filter(p => p.officeId === officeId);
export const getCoursesByOfficeId = (officeId) => COURSES.filter(c => c.officeId === officeId);
export const getRentalsByOfficeId = (officeId) => RENTALS.filter(r => r.officeId === officeId);
export const getEventsByOfficeId = (officeId) => EVENTS.filter(e => e.officeId === officeId);
export const getUpcomingEvents = () => {
  const today = new Date().toISOString().split('T')[0];
  return EVENTS.filter(e => e.date >= today && e.status === 'active')
    .sort((a, b) => new Date(a.date) - new Date(b.date));
};
export const getAllEvents = () => EVENTS;
export const getCourseScheduleByOfficeId = (officeId) => {
  const courses = getCoursesByOfficeId(officeId);
  const courseIds = courses.map(c => c.id);
  return COURSE_SCHEDULE.filter(s => courseIds.includes(s.courseId));
};

// Recensioni
export const getReviewsByOfficeId = (officeId) => {
  return REVIEWS.filter(r => r.officeId === officeId).map(review => {
    const user = getUserById(review.userId);
    return {
      ...review,
      userName: user ? `${user.firstName} ${user.lastName?.charAt(0) || ''}.` : 'Utente',
      userAvatar: user?.avatar,
    };
  });
};
export const getReviewsByUserId = (userId) => REVIEWS.filter(r => r.userId === userId);

// Notifiche
export const getNotificationsByUserId = (userId) => NOTIFICATIONS.filter(n => n.userId === userId);
export const getUnreadNotificationsCount = (userId) => NOTIFICATIONS.filter(n => n.userId === userId && !n.read).length;

// Preferiti
export const getFavoritesByUserId = (userId) => FAVORITES.filter(f => f.userId === userId);
export const getUserFavoriteOffices = (userId) => {
  const favs = getFavoritesByUserId(userId).filter(f => f.itemType === 'office');
  return favs.map(f => getOfficeById(f.itemId)).filter(Boolean);
};

// Metodi di pagamento
export const getPaymentMethodsByUserId = (userId) => PAYMENT_METHODS.filter(p => p.userId === userId);

// Transazioni
export const getBookingsByOfficeId = (officeId) => BOOKINGS.filter(b => b.officeId === officeId);
export const getOrdersByOfficeId = (officeId) => ORDERS.filter(o => o.officeId === officeId);
export const getBookingsByUserId = (userId) => BOOKINGS.filter(b => b.userId === userId);
export const getOrdersByUserId = (userId) => ORDERS.filter(o => o.userId === userId);

// Moduli
export const getModuleConfig = (moduleId) => MODULES[moduleId] || null;
export const isModuleEnabledForOffice = (officeId, moduleId) => {
  const office = getOfficeById(officeId);
  return office?.enabledModules?.includes(moduleId) || false;
};
export const isSubmoduleEnabledForOffice = (officeId, moduleId, submoduleId) => {
  const office = getOfficeById(officeId);
  return office?.enabledSubmodules?.[moduleId]?.includes(submoduleId) || false;
};
export const getEnabledModulesForOffice = (officeId) => {
  const office = getOfficeById(officeId);
  if (!office?.enabledModules) return [];
  return office.enabledModules.map(id => ({ id, ...MODULES[id] }));
};

// Prezzi giornalieri
export const getDailyPricing = (officeId, resourceId) => {
  const key = `${officeId}-${resourceId}`;
  return DAILY_PRICING[key] || null;
};
export const calculateDailyPrice = (officeId, resourceId, date) => {
  const pricing = getDailyPricing(officeId, resourceId);
  if (!pricing) return null;
  
  const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
  
  // Prezzo personalizzato per data specifica
  if (pricing.customDates?.[dateStr]) {
    return pricing.customDates[dateStr];
  }
  
  const d = new Date(dateStr);
  const dayOfWeek = d.getDay();
  const month = d.getMonth() + 1;
  
  let price = pricing.basePrice;
  
  // Maggiorazione weekend
  if ((dayOfWeek === 0 || dayOfWeek === 6) && pricing.weekendMarkup) {
    price *= (1 + pricing.weekendMarkup / 100);
  }
  
  // Prezzi stagionali
  if (pricing.seasonalPricing) {
    for (const season of pricing.seasonalPricing) {
      if (month >= season.startMonth && month <= season.endMonth) {
        price *= (1 + season.markup / 100);
        break;
      }
    }
  }
  
  return Math.round(price);
};

// Ricerca
export const searchOffices = ({ categorySlug, citySlug, moduleId, query }) => {
  let results = [...OFFICES];
  
  if (categorySlug) {
    results = results.filter(o => o.categorySlug === categorySlug);
  }
  if (citySlug) {
    const city = CITIES.find(c => c.slug === citySlug);
    if (city) {
      // Filtra per città (semplificato - in produzione usare coordinate)
      results = results.filter(o => o.address?.toLowerCase().includes(city.name.toLowerCase()));
    }
  }
  if (moduleId) {
    results = results.filter(o => o.enabledModules?.includes(moduleId));
  }
  if (query) {
    const q = query.toLowerCase();
    results = results.filter(o => 
      o.name.toLowerCase().includes(q) ||
      o.categorySlug.includes(q)
    );
  }
  
  return results;
};

// Statistiche per Office
export const getOfficeStats = (officeId) => {
  const bookings = getBookingsByOfficeId(officeId);
  const orders = getOrdersByOfficeId(officeId);
  
  const confirmedBookings = bookings.filter(b => b.status === 'confirmed' || b.status === 'completed');
  const completedOrders = orders.filter(o => o.status === 'delivered');
  
  return {
    totalBookings: bookings.length,
    confirmedBookings: confirmedBookings.length,
    totalOrders: orders.length,
    completedOrders: completedOrders.length,
    bookingsRevenue: confirmedBookings.reduce((sum, b) => sum + (b.total || 0), 0),
    ordersRevenue: completedOrders.reduce((sum, o) => sum + (o.total || 0), 0),
  };
};

// Enrichment functions
export const enrichBooking = (booking) => {
  const office = getOfficeById(booking.officeId);
  const service = SERVICES.find(s => s.id === booking.serviceId);
  const resource = RESOURCES.find(r => r.id === booking.resourceId);
  const user = getUserById(booking.userId);
  
  return {
    ...booking,
    officeName: office?.name,
    serviceName: service?.name,
    resourceName: resource?.name,
    customerName: user ? `${user.firstName} ${user.lastName}` : 'Cliente',
  };
};

export const enrichOrder = (order) => {
  const office = getOfficeById(order.officeId);
  const user = getUserById(order.userId);
  const items = order.items?.map(item => {
    const product = PRODUCTS.find(p => p.id === item.productId);
    return { ...item, productName: product?.name, productPrice: product?.price };
  }) || [];
  
  return {
    ...order,
    officeName: office?.name,
    customerName: user ? `${user.firstName} ${user.lastName}` : 'Cliente',
    enrichedItems: items,
  };
};

// Getters aggiuntivi
export const getAllBookingsForOffice = (officeId) => getBookingsByOfficeId(officeId).map(enrichBooking);
export const getAllOrdersForOffice = (officeId) => getOrdersByOfficeId(officeId).map(enrichOrder);
export const getResourcesForOffice = (officeId) => getResourcesByOfficeId(officeId);

// ============================================================================
// SEZIONE 16: COSTANTI E CONFIGURAZIONI DI SISTEMA
// ============================================================================
// Costanti utilizzate nell'applicazione

// Tipi di risorsa disponibili nel sistema
export const RESOURCE_TYPES = {
  operator: { id: 'operator', name: 'Operatori', icon: 'User', color: 'emerald' },
  instructor: { id: 'instructor', name: 'Istruttori', icon: 'GraduationCap', color: 'purple' },
  room: { id: 'room', name: 'Sale/Camere', icon: 'DoorOpen', color: 'blue' },
  table: { id: 'table', name: 'Tavoli', icon: 'UtensilsCrossed', color: 'rose' },
  vehicle: { id: 'vehicle', name: 'Veicoli', icon: 'Car', color: 'amber' },
  boat: { id: 'boat', name: 'Barche', icon: 'Ship', color: 'cyan' },
  apartment: { id: 'apartment', name: 'Appartamenti', icon: 'Home', color: 'indigo' },
  equipment: { id: 'equipment', name: 'Attrezzature', icon: 'Wrench', color: 'slate' },
  area: { id: 'area', name: 'Aree', icon: 'LayoutGrid', color: 'orange' },
};

// Tipi di fulfillment per ordini e-commerce
export const FULFILLMENT_TYPES = ['shipping', 'delivery', 'takeaway'];

// Tipi di orari speciali
export const SPECIAL_HOURS_TYPES = {
  HOLIDAY: { id: 'holiday', name: 'Festività', color: 'red' },
  SPECIAL_EVENT: { id: 'special_event', name: 'Evento Speciale', color: 'purple' },
  MAINTENANCE: { id: 'maintenance', name: 'Manutenzione', color: 'amber' },
  CLOSURE: { id: 'closure', name: 'Chiusura', color: 'gray' },
};

// Piani Partner
export const PARTNER_PLANS = [
  { id: 'free', name: 'Free', price: 0, features: ['1 sede', 'Funzionalità base'] },
  { id: 'pro', name: 'Pro', price: 29, features: ['3 sedi', 'Analytics', 'Priorità'] },
  { id: 'business', name: 'Business', price: 79, features: ['Sedi illimitate', 'API', 'Supporto dedicato'] },
];

// ============================================================================
// SEZIONE 17: FUNZIONI HELPER AGGIUNTIVE
// ============================================================================

// Getters per ID singolo
export const getBookingById = (id) => {
  const booking = BOOKINGS.find(b => b.id === id);
  return booking ? enrichBooking(booking) : null;
};
export const getOrderById = (id) => {
  const order = ORDERS.find(o => o.id === id);
  return order ? enrichOrder(order) : null;
};

// Orari lavorativi
export const getWorkingHoursByOfficeId = (officeId) => {
  const office = getOfficeById(officeId);
  return office?.hours || {};
};

// Costi spedizione per office
export const getCostsByOfficeId = (officeId) => {
  const office = getOfficeById(officeId);
  return office?.moduleSettings?.ecommerce?.shipping?.zones || [];
};

// Geografia
export const getCountryBySlug = (slug) => COUNTRIES.find(c => c.slug === slug);
export const getRegionsByCountry = (countryCode) => REGIONS.filter(r => r.countryCode === countryCode);
export const getCitiesByRegion = (regionId) => CITIES.filter(c => c.regionId === regionId);
export const getCityBySlugSimple = (slug) => CITIES.find(c => c.slug === slug);
export const getRegionBySlugSimple = (slug) => REGIONS.find(r => r.slug === slug);

// Configurazione Office
export const getOfficeConfig = (officeId) => {
  const office = getOfficeById(officeId);
  return office?.moduleSettings || {};
};

// Funzione per aggiornare moduli office (simulazione - in produzione sarà API)
export const updateOfficeModules = (officeId, enabledModules, moduleSettings) => {
  const office = OFFICES.find(o => o.id === officeId);
  if (office) {
    office.enabledModules = enabledModules;
    office.moduleSettings = { ...office.moduleSettings, ...moduleSettings };
  }
  return office;
};

// ============================================================================
// FINE DATABASE
// ============================================================================
